import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Save, UploadCloud, Loader2, KeyRound, ArrowLeft, ChevronRight, Eye, X, Copy, ExternalLink, Printer, RefreshCw, Download, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { gbp, tierUnitPrice } from "@/lib/pricing";
import { FlavorsAdmin } from "@/components/admin/FlavorsAdmin";
import { RestockWaitlist } from "@/components/admin/RestockWaitlist";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent, useCategories, DEFAULT_CATEGORIES } from "@/context/SiteContentContext";
import { DiscountCodesAdmin } from "@/components/admin/DiscountCodesAdmin";
import { AffiliateAdmin } from "@/components/admin/AffiliateAdmin";
import { COPY_DEFAULTS } from "@/lib/copy";
import { OrderFilters, applyOrderFilters, EMPTY_ORDER_FILTERS } from "@/components/admin/OrderFilters";
import { downloadOrdersCsv } from "@/lib/ordersCsv";
import { ShipmentImport } from "@/components/admin/ShipmentImport";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const TABS = ["overview", "orders", "flavors", "restock", "pricing", "multipacks", "content", "shipping", "users", "dropshippers", "security", "account"];
const TAB_LABELS = { dropshippers: "Dynamic Pricing", multipacks: "Multipacks", flavors: "Products & Stock", restock: "Restock Demand" };

export default function Admin() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.includes(t) ? t : "overview";
  });

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <div className="grain" />
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1500px] items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <img src="/api/files/dreamlabs/migrated/logo-b3d7c5ce2ec74890a8f19a1940418963.png" alt="DreamLabs UK" className="h-9 w-auto" />
            <span className="mono border border-border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground">Store ↗</Link>
            <button data-testid="admin-logout" onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-1.5 mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground"><LogOut className="h-3.5 w-3.5" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="relative z-10 mx-auto max-w-[1500px] px-6 py-10">
        <h1 className="font-heading text-4xl font-black tracking-tighter">Dashboard</h1>
        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="flex flex-wrap gap-1 rounded-none bg-transparent p-0">
            {TABS.map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`}
                className="rounded-none border border-transparent px-4 py-2 mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:border-border data-[state=active]:bg-card">
                {TAB_LABELS[t] || t}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="orders"><Orders /></TabsContent>
          <TabsContent value="flavors"><FlavorsAdmin /></TabsContent>
          <TabsContent value="restock">{tab === "restock" && <RestockWaitlist />}</TabsContent>
          <TabsContent value="pricing"><Pricing /></TabsContent>
          <TabsContent value="multipacks"><Multipacks /></TabsContent>
          <TabsContent value="content"><Content /></TabsContent>
          <TabsContent value="shipping"><Shipping /></TabsContent>
          <TabsContent value="users"><Users /></TabsContent>
          <TabsContent value="dropshippers">{tab === "dropshippers" && <Dropshippers />}</TabsContent>
          <TabsContent value="security"><Security /></TabsContent>
          <TabsContent value="account"><Account /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-semibold">{title}</h2>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return null;
  const s = stats.sales || {};
  const cards = [
    ["Total Orders", stats.total_orders], ["Paid", stats.paid_orders], ["Pending", stats.pending_orders],
    ["Revenue", gbp(stats.revenue)], ["Flavors", stats.flavors], ["Low Stock", stats.low_stock ?? 0],
  ];
  return (
    <div data-testid="admin-overview">
      <div className="mt-6 border border-border" data-testid="admin-sales-snapshot">
        <div className="flex items-center justify-between border-b border-border bg-card px-5 py-3">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Takings — paid orders</div>
          <span className="mono text-[10px] text-muted-foreground">avg order {gbp(s.aov || 0)}</span>
        </div>
        <div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-3">
          {[
            ["Last 7 days", s.revenue_7d, s.paid_7d],
            ["Last 30 days", s.revenue_30d, s.paid_30d],
            ["All time", s.revenue_all ?? stats.revenue, stats.paid_orders],
          ].map(([l, rev, count]) => (
            <div key={l} className="bg-card p-6" data-testid={`sales-${String(l).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{l}</div>
              <div className="mt-2 font-heading text-3xl font-black text-primary">{gbp(rev || 0)}</div>
              <div className="mono mt-1 text-[11px] text-muted-foreground">{count || 0} paid order{(count || 0) === 1 ? "" : "s"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-px border border-border bg-border md:grid-cols-3 lg:grid-cols-6" data-testid="admin-stats">
        {cards.map(([l, v]) => (
          <div key={l} className="bg-card p-6">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{l}</div>
            <div className="mt-2 font-heading text-3xl font-black">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EVENT_LABELS = {
  failed_login: "Failed login",
  rate_limited: "Rate limit hit",
  invalid_webhook_sig: "Bad webhook signature",
};
const EVENT_COLORS = {
  failed_login: "text-amber-400",
  rate_limited: "text-rose-400",
  invalid_webhook_sig: "text-red-500",
};

function BtcpayHealthCard() {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/btcpay/health"); setH(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }, []);
  useEffect(() => { check(); }, [check]);
  const w = h?.watcher || {};
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-border bg-card p-4" data-testid="btcpay-health">
      <div>
        <div className="font-heading text-sm font-bold">BTCPay payment server</div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Checked every 2 minutes. You get a Telegram alert when BTCPay stops answering or a coin's node isn't ready, and another when it recovers.
          {h && !h.alerts_enabled && <span className="text-destructive"> Alerts are disabled in the API .env (BTCPAY_HEALTH_ALERTS).</span>}
        </p>
        <div className="mt-1 mono text-[11px]" data-testid="btcpay-health-status">
          {h === null ? "Checking…" : h.ok
            ? <span className="text-emerald-600">● Up — invoices can be created</span>
            : <span className="text-destructive">● Down — {h.detail}</span>}
          {w.last_checked_at && <span className="text-muted-foreground"> · watcher last check {new Date(w.last_checked_at).toLocaleTimeString()}{w.status === "down" && w.down_since ? ` · down since ${new Date(w.down_since).toLocaleString()}` : ""}</span>}
        </div>
      </div>
      <button data-testid="btcpay-health-check-btn" disabled={busy} onClick={check}
        className="inline-flex items-center gap-1.5 border border-border px-4 py-2 text-xs font-medium hover:border-foreground disabled:opacity-60">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Check now
      </button>
    </div>
  );
}

function Security() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/security-events").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading && !data) return <div className="mt-6 mono text-xs text-muted-foreground">Loading…</div>;
  if (!data) return null;
  const s = data.summary_24h;
  const cards = [
    ["Events (24h)", s.total, "text-foreground"],
    ["Failed logins (24h)", s.failed_login, EVENT_COLORS.failed_login],
    ["Rate-limit hits (24h)", s.rate_limited, EVENT_COLORS.rate_limited],
    ["Bad webhooks (24h)", s.invalid_webhook_sig, EVENT_COLORS.invalid_webhook_sig],
  ];
  return (
    <div data-testid="admin-security">
      <div className="mt-6 flex items-center justify-between">
        <h2 className="font-heading text-2xl font-semibold">Security events</h2>
        <button data-testid="security-refresh" onClick={load}
          className="mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground">↻ Refresh</button>
      </div>
      <p className="mt-1 mono text-[11px] text-muted-foreground">
        Failed logins, brute-force rate-limit blocks and forged payment webhooks. Auto-clears after 30 days.
      </p>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-border bg-card p-4" data-testid="telegram-alert-test">
        <div>
          <div className="font-heading text-sm font-bold">Telegram admin alerts</div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Paid orders, low stock and stuck payments are DM'd to the admin Telegram IDs. Send a test to confirm the bot token on this server works.</p>
        </div>
        <button data-testid="telegram-alert-test-btn" disabled={testing} onClick={async () => {
          setTesting(true);
          try { const { data } = await api.post("/admin/telegram/test-alert"); toast.success(`Test alert sent to ${data.sent} of ${data.admins} admin${data.admins === 1 ? "" : "s"} — check Telegram`); }
          catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
        }} className="inline-flex items-center gap-1.5 bg-foreground px-4 py-2 text-xs font-medium text-background disabled:opacity-60">
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test Telegram alert
        </button>
      </div>

      <BtcpayHealthCard />

      <div className="mt-4 grid grid-cols-2 gap-px border border-border bg-border lg:grid-cols-4" data-testid="security-stats">
        {cards.map(([l, v, c]) => (
          <div key={l} className="bg-card p-6">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{l}</div>
            <div className={`mt-2 font-heading text-3xl font-black ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      {data.top_ips_24h?.length > 0 && (
        <div className="mt-6">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Most active IPs (24h)</div>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="security-top-ips">
            {data.top_ips_24h.map((r) => (
              <span key={r.ip} className="mono text-[11px] border border-border bg-card px-2.5 py-1">
                {r.ip} <span className="text-muted-foreground">· {r.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 border border-border" data-testid="security-events-table">
        <div className="grid grid-cols-[130px_1fr_150px] gap-2 border-b border-border bg-card px-4 py-2 mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <span>Type</span><span>Detail</span><span>When</span>
        </div>
        {data.events.length === 0 && (
          <div className="px-4 py-8 text-center mono text-xs text-muted-foreground" data-testid="security-empty">
            No security events recorded yet. That's a good sign.
          </div>
        )}
        {data.events.map((e, i) => (
          <div key={i} className="grid grid-cols-[130px_1fr_150px] gap-2 border-b border-border/50 px-4 py-2.5 text-sm">
            <span className={`mono text-[11px] font-semibold ${EVENT_COLORS[e.type] || "text-foreground"}`}>
              {EVENT_LABELS[e.type] || e.type}
            </span>
            <span className="min-w-0 truncate text-muted-foreground">
              <span className="mono text-[11px] text-foreground">{e.ip}</span>
              {e.detail ? <span className="ml-2">{e.detail}</span> : null}
            </span>
            <span className="mono text-[11px] text-muted-foreground">
              {new Date(e.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


const ORDER_ITEM_LABEL = (it) => {
  if (it.type === "single" || it.type === "offer") return `${it.quantity}× ${it.flavor_name} — ${it.size_label} (${it.base})${it.type === "offer" ? " · offer" : ""}`;
  if (it.type === "multipack") return `${it.quantity}× ${it.multipack_name || "Multipack"}`;
  if (it.type === "guest") { const o = (it.options || []).map((x) => x.label || x).filter(Boolean).join(", "); return `Premium · ${it.quantity}× ${it.flavor_name}${it.size_label ? ` — ${it.size_label}` : ""}${o ? ` (${o})` : ""}`; }
  return `${it.type === "economy" ? "Economy Flavours" : "Build Your Box"} · ${it.bottle_count || 0}× ${it.size_label || "Bottle"}`;
};

const boostTag = (b) => (b === "x2" ? "×2 Double" : b === "x3" ? "×3 Triple" : "");

function DeliveryBlock({ order }) {
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  const addr = order.address || "";
  const isPgp = addr.includes("BEGIN PGP MESSAGE");
  const isLink = /^https?:\/\//i.test(addr.trim());
  return (
    <div data-testid="order-detail-delivery">
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Delivery</div>
      <div className="mt-1 text-sm">Ship to: <span className="font-semibold">{order.country}</span>{order.shipping_zone ? <span className="text-muted-foreground"> · {order.shipping_zone}</span> : null}</div>
      {isPgp ? (
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">🔐 PGP-encrypted delivery details</span>
            <button data-testid="copy-delivery" onClick={() => copy(addr)} className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[11px] hover:bg-muted"><Copy className="h-3 w-3" /> Copy</button>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {order.affiliate?.pgp_fingerprint
              ? <>Encrypted to <b>{order.affiliate.brand_name}</b>'s key <span className="mono text-foreground" data-testid="order-detail-pgp-key">{order.affiliate.pgp_fingerprint}</span> — use that private key (or ask the partner to decrypt).</>
              : "Decrypt with your PGP private key to reveal the name & address."}
          </p>
          <pre className="mono mt-2 max-h-52 overflow-auto whitespace-pre-wrap break-all border border-border bg-muted/40 p-3 text-[10px] leading-tight">{addr}</pre>
        </div>
      ) : isLink ? (
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">🔗 One-time secure link{order.city && order.city !== "N/A" ? ` · ${order.city}` : ""}</span>
            <button data-testid="copy-delivery" onClick={() => copy(addr)} className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[11px] hover:bg-muted"><Copy className="h-3 w-3" /> Copy</button>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">Opens once, then self-destructs — open it as soon as you're ready to pack.</p>
          <a data-testid="open-delivery-link" href={addr} target="_blank" rel="noopener noreferrer" className="mono mt-2 inline-flex max-w-full items-center gap-1 break-all border border-border bg-muted/40 p-3 text-[11px] hover:bg-muted">{addr} <ExternalLink className="h-3 w-3 shrink-0" /></a>
        </div>
      ) : (
        <div className="mt-2 text-sm">
          <div className="font-semibold">{order.customer_name}</div>
          <div className="text-muted-foreground">{[addr, order.city, order.postcode].filter((x) => x && x !== "N/A" && x !== "—").join(", ")}</div>
        </div>
      )}
    </div>
  );
}

function SiteCopyEditor({ c, set }) {
  const overrides = c.site_copy || {};
  const [open, setOpen] = useState(null);
  const reset = (key) => { const next = { ...overrides }; delete next[key]; set("site_copy", next); };
  const setText = (key, val) => (val === COPY_DEFAULTS[key].default ? reset(key) : set("site_copy", { ...overrides, [key]: val }));
  const pages = [...new Set(Object.values(COPY_DEFAULTS).map((m) => m.page))];
  return (
    <div className="border border-border p-5" data-testid="content-site-copy-section">
      <div className="mono mb-1 text-[11px] uppercase tracking-[0.2em]">Website wording</div>
      <p className="mb-4 text-xs text-muted-foreground">
        Every fixed sentence on the How to Order, Shipping, Checkout, Track, order status and PGP pages. Press <span className="text-foreground">Edit this text</span> to load the current wording into the box and change what you like; empty = default (shown greyed). Formatting: <span className="mono text-foreground">**bold**</span>, <span className="mono text-foreground">[link text](https://… or /track)</span>, blank line = new paragraph. <span className="mono text-foreground">{"{brand}"}</span> becomes the shop name (so partner shops stay branded).
      </p>
      <div className="space-y-2">
        {pages.map((page) => {
          const keys = Object.keys(COPY_DEFAULTS).filter((k) => COPY_DEFAULTS[k].page === page);
          const editedCount = keys.filter((k) => overrides[k]).length;
          const isOpen = open === page;
          return (
            <div key={page} className="border border-border" data-testid={`site-copy-group-${page.replace(/\W+/g, "-").toLowerCase()}`}>
              <button type="button" onClick={() => setOpen(isOpen ? null : page)} className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold hover:bg-muted/40">
                <span>{page} <span className="mono ml-2 text-[10px] font-normal uppercase tracking-[0.12em] text-muted-foreground">{keys.length} texts{editedCount ? ` · ${editedCount} edited` : ""}</span></span>
                <span className="mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{isOpen ? "Hide" : "Edit"}</span>
              </button>
              {isOpen && (
                <div className="grid gap-4 border-t border-border p-4 lg:grid-cols-2">
                  {keys.map((key) => {
                    const meta = COPY_DEFAULTS[key]; const edited = !!overrides[key];
                    return (
                      <div key={key} data-testid={`site-copy-${key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{meta.label}{edited && <span className="ml-2 text-primary">· edited</span>}</label>
                          {edited
                            ? <button type="button" data-testid={`site-copy-reset-${key}`} onClick={() => reset(key)} className="mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground">Reset to default</button>
                            : <button type="button" data-testid={`site-copy-edit-${key}`} onClick={() => set("site_copy", { ...overrides, [key]: meta.default })} className="mono text-[10px] uppercase tracking-[0.1em] text-primary hover:underline">Edit this text</button>}
                        </div>
                        <textarea data-testid={`site-copy-input-${key}`} value={overrides[key] || ""} placeholder={meta.default} onChange={(e) => setText(key, e.target.value)}
                          rows={Math.min(6, Math.max(1, Math.ceil(meta.default.length / 90)))}
                          className="mt-1 w-full rounded-none border border-border bg-background p-2 text-sm placeholder:text-muted-foreground/60" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BotWordingEditor({ c, set }) {
  const defaults = c.bot_text_defaults || {};
  const overrides = c.bot_texts || {};
  const reset = (key) => { const next = { ...overrides }; delete next[key]; set("bot_texts", next); };
  const setText = (key, val) => (val === defaults[key].default ? reset(key) : set("bot_texts", { ...overrides, [key]: val }));
  const keys = Object.keys(defaults);
  if (!keys.length) return null;
  return (
    <div className="border border-border p-5" data-testid="content-bot-wording-section">
      <div className="mono mb-1 text-[11px] uppercase tracking-[0.2em]">Telegram bot wording</div>
      <p className="mb-4 text-xs text-muted-foreground">
        Every message the ordering bot shows customers. Press <span className="text-foreground">Edit this text</span> to load the current wording into the box and change what you like; empty = default (shown greyed). Keep the <span className="mono text-foreground">{"{placeholders}"}</span> listed under each box — the bot fills them in.
        Telegram formatting works: *bold*, _italic_. Changes reach the bot within a minute of saving — no redeploy.
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        {keys.map((key) => {
          const meta = defaults[key]; const edited = overrides[key] != null && overrides[key] !== "";
          return (
            <div key={key} data-testid={`bot-text-${key}`}>
              <div className="flex items-center justify-between gap-2">
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{meta.label}{edited && <span className="ml-2 text-primary">· edited</span>}</label>
                {edited
                  ? <button type="button" data-testid={`bot-text-reset-${key}`} onClick={() => reset(key)} className="mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground">Reset to default</button>
                  : <button type="button" data-testid={`bot-text-edit-${key}`} onClick={() => set("bot_texts", { ...overrides, [key]: meta.default })} className="mono text-[10px] uppercase tracking-[0.1em] text-primary hover:underline">Edit this text</button>}
              </div>
              <textarea data-testid={`bot-text-input-${key}`} value={overrides[key] || ""} placeholder={meta.default} onChange={(e) => setText(key, e.target.value)}
                rows={Math.min(8, Math.max(2, (meta.default.match(/\n/g) || []).length + 1))}
                className="mt-1 w-full rounded-none border border-border bg-background p-2 text-sm placeholder:text-muted-foreground/60" />
              {meta.vars && <div className="mono mt-1 text-[10px] text-muted-foreground">Placeholders: <span className="text-foreground">{meta.vars}</span></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PartnerPayoutBlock({ order }) {
  const [p, setP] = useState(order.affiliate_payout || null);
  const [busy, setBusy] = useState(false);
  const run = async (path, okMsg) => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/orders/${order.id}/affiliate-payout${path}`); setP(data); toast.success(okMsg); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-3 text-xs" data-testid="order-detail-payout">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Partner payout · {order.affiliate.brand_name} · cut {gbp(order.affiliate.commission_amount || 0)}</div>
        <div className="flex gap-2">
          {p?.status === "created" && <button data-testid="order-payout-refresh" disabled={busy} onClick={() => run("/refresh", "Payout state refreshed")} className="border border-border px-2 py-1 hover:bg-muted">Refresh state</button>}
          {p?.status !== "created" && <button data-testid="order-payout-retry" disabled={busy} onClick={() => run("", "Payout created in BTCPay")} className="border border-primary px-2 py-1 text-primary hover:bg-primary/10">{p?.status === "failed" ? "Retry payout" : "Send payout now"}</button>}
        </div>
      </div>
      <div className="mono mt-2" data-testid="order-payout-status">
        {!p && <span className="text-muted-foreground">No automatic payout yet.</span>}
        {p?.status === "created" && <span className="text-green-700">Sent {Number(p.crypto_amount).toFixed(8)} {p.currency} ({gbp(p.amount_gbp)} after {gbp(p.fee_gbp || 0)} fee allowance) → {p.destination} · BTCPay {p.payout_id} · {p.state}{p.txid ? ` · tx ${p.txid}` : ""}</span>}
        {p?.status === "failed" && <span className="text-destructive">Failed: {p.error}</span>}
        {p?.status === "skipped" && <span className="text-muted-foreground">Skipped: {p.reason}</span>}
        {p?.status === "sending" && <span className="text-muted-foreground">Sending…</span>}
      </div>
    </div>
  );
}

function OrderDetail({ order, onClose }) {
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  const realEmail = order.email && !order.email.endsWith("@telegram.dreamlabs.uk");
  const when = order.created_at ? new Date(order.created_at).toLocaleString("en-GB") : "";
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8" data-testid="order-detail-modal" onClick={onClose}>
      <div className="w-full max-w-2xl border border-border bg-background shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between border-b border-border bg-muted px-5 py-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Order</div>
            <div className="mono text-lg font-bold">{order.order_number}</div>
          </div>
          <button data-testid="order-detail-close" onClick={onClose} className="border border-border p-2 hover:bg-background"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[75vh] space-y-6 overflow-y-auto p-5">
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="border border-border px-2 py-1">Placed: {when}</span>
            <span className="border border-border px-2 py-1">Payment: <b>{order.payment_status}</b></span>
            <span className="border border-border px-2 py-1">Fulfilment: <b>{order.fulfillment_status}</b></span>
            {order.is_wholesale ? <span className="border border-primary px-2 py-1 text-primary">Wholesale</span> : null}
            {order.affiliate?.slug ? <span className="border border-primary px-2 py-1 text-primary" data-testid="order-detail-affiliate">Via {order.affiliate.brand_name} · commission {gbp(order.affiliate.commission_amount || 0)}</span> : null}
          </div>

          <DeliveryBlock order={order} />

          {order.affiliate?.partner_id && order.payment_status === "paid" && <PartnerPayoutBlock order={order} />}

          {realEmail && (
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Contact email</div>
              <div className="mt-1 text-sm">{order.email}</div>
            </div>
          )}

          <div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Items</div>
            <div className="mt-2 divide-y divide-border border border-border">
              {(order.items || []).map((it, i) => (
                <div key={i} className="p-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="font-medium">{ORDER_ITEM_LABEL(it)}</span>
                    <span className="mono shrink-0">{gbp(it.line_total ?? it.box_price ?? (it.unit_price || 0) * (it.quantity || 1))}</span>
                  </div>
                  {(it.selections || []).length > 0 && (
                    <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                      {it.selections.map((s, j) => (
                        <li key={j}>{s.quantity}× {s.flavor_name} <span className="opacity-70">({[s.base || (it.type === "economy" ? "Economy" : ""), boostTag(s.boost) || (s.boost_label && s.boost !== "standard" ? s.boost_label : "")].filter(Boolean).join(" · ") || "Standard"})</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="mono">{gbp(order.subtotal)}</span></div>
              {order.discount && order.discount.amount ? <div className="flex justify-between text-primary"><span>Discount{order.discount.pct ? ` (${Math.round(order.discount.pct * 100)}%)` : ""}</span><span className="mono">−{gbp(order.discount.amount)}</span></div> : null}
              {order.discount && order.discount.guest_bundle_saving ? <div className="flex justify-between text-primary"><span>Guest bundle saving{order.discount.guest_bundle_pct ? ` (${order.discount.guest_bundle_pct}%)` : ""}</span><span className="mono">−{gbp(order.discount.guest_bundle_saving)}</span></div> : null}
              <div className="flex justify-between text-muted-foreground"><span>Shipping{order.total_weight_g ? ` · ${(order.total_weight_g / 1000).toFixed(2)} kg` : ""}</span><span className="mono">{gbp(order.shipping)}</span></div>
              <div className="flex justify-between font-bold"><span>Total</span><span className="mono">{gbp(order.total)} {order.currency}</span></div>
            </div>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Payment</div>
            <div className="mt-1 space-y-1 text-sm">
              <div>{order.currency} · {order.crypto_network || "—"}{order.crypto_amount ? ` · ${order.crypto_amount} ${order.currency}` : ""}</div>
              {order.deposit_address && (
                <div className="flex items-center gap-2">
                  <span className="mono break-all text-xs text-muted-foreground">{order.deposit_address}</span>
                  <button onClick={() => copy(order.deposit_address)} className="border border-border p-1 hover:bg-muted"><Copy className="h-3 w-3" /></button>
                </div>
              )}
              {order.checkout_url && <a href={order.checkout_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs underline">BTCPay invoice <ExternalLink className="h-3 w-3" /></a>}
              {order.payment_error && <div className="text-xs text-destructive">{order.payment_error}</div>}
            </div>
          </div>

          {order.tracking_number && (
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Tracking</div>
              <div className="mt-1 mono text-sm">{order.tracking_number}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [tracking, setTracking] = useState({});
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, field, value) => {
    await api.put(`/admin/orders/${id}`, { [field]: value });
    load();
    toast.success(field === "fulfillment_status" && value === "shipped" ? "Marked shipped — dispatch email sent" : "Order updated");
  };
  const saveTracking = async (id) => {
    await api.put(`/admin/orders/${id}`, { tracking_number: tracking[id] || "" });
    toast.success("Tracking saved");
  };
  const [rechecking, setRechecking] = useState({});
  const [filters, setFilters] = useState(EMPTY_ORDER_FILTERS);
  const visible = applyOrderFilters(orders, filters);
  const [picked, setPicked] = useState({});
  const pickedVisible = visible.filter((o) => picked[o.id]);
  const allVisiblePicked = visible.length > 0 && pickedVisible.length === visible.length;
  const toggleAll = () => setPicked(allVisiblePicked ? {} : Object.fromEntries(visible.map((o) => [o.id, true])));
  const exportCsv = () => {
    const rows = pickedVisible.length ? pickedVisible : visible;
    if (!rows.length) { toast.error("No orders to export"); return; }
    downloadOrdersCsv(rows, pickedVisible.length ? "dreamlabs-orders-selected" : "dreamlabs-orders");
    toast.success(`Exported ${rows.length} order${rows.length === 1 ? "" : "s"}`);
  };
  const [rmService, setRmService] = useState("Tracked 24");
  const [rmBusy, setRmBusy] = useState(false);
  const [sheetBusy, setSheetBusy] = useState(false);
  const exportXlsx = async (path, prefix, label, setBusy) => {
    const rows = pickedVisible.length ? pickedVisible : visible;
    if (!rows.length) { toast.error("No orders to export"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(path, { order_ids: rows.map((o) => o.id), service: rmService }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${prefix}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`${label} ready — ${rows.length} order${rows.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const exportRoyalMail = () => exportXlsx("/admin/orders/export/royal-mail", "royal-mail-orders", "Royal Mail sheet", setRmBusy);
  const exportOrderSheet = () => exportXlsx("/admin/orders/export/order-sheet", "order-sheet", "Order sheet", setSheetBusy);
  const retryPayment = async (o) => {
    setRechecking((r) => ({ ...r, [o.id]: true }));
    try {
      await api.post(`/admin/orders/${o.id}/retry-payment`);
      await load();
      toast.success(`${o.order_number}: BTCPay invoice created — the customer's order link now shows payment details`);
    } catch (e) { toast.error(apiError(e)); } finally { setRechecking((r) => ({ ...r, [o.id]: false })); }
  };
  const recheck = async (o) => {
    setRechecking((r) => ({ ...r, [o.id]: true }));
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/refresh-payment`);
      await load();
      toast[data.payment_status === "paid" ? "success" : "info"](`${o.order_number}: ${data.payment_status.replace("_", " ")} (BTCPay ${data.btcpay_status || "?"}${data.btcpay_additional_status && data.btcpay_additional_status !== "None" ? " · " + data.btcpay_additional_status : ""})`);
    } catch (e) { toast.error(apiError(e)); } finally { setRechecking((r) => ({ ...r, [o.id]: false })); }
  };
  return (
    <Section title="Orders">
      <OrderFilters filters={filters} onChange={setFilters} orders={orders} shown={visible.length} />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="mono text-[11px] text-muted-foreground" data-testid="orders-selected-count">
          {pickedVisible.length ? `${pickedVisible.length} selected` : "Nothing selected — export uses all orders shown"}
        </div>
        <div className="flex flex-wrap gap-2">
          <ShipmentImport onApplied={load} />
          {pickedVisible.length > 0 && (
            <button data-testid="orders-clear-selection" onClick={() => setPicked({})} className="border border-border px-3 py-2 text-xs hover:border-primary">Clear selection</button>
          )}
          <button data-testid="orders-export-csv" onClick={exportCsv} className="inline-flex items-center gap-1.5 bg-foreground px-4 py-2 text-xs font-medium text-background">
            <Download className="h-3.5 w-3.5" /> Export CSV ({pickedVisible.length || visible.length})
          </button>
          <button data-testid="orders-export-order-sheet" onClick={exportOrderSheet} disabled={sheetBusy} className="inline-flex items-center gap-1.5 border border-foreground px-4 py-2 text-xs font-medium disabled:opacity-60">
            {sheetBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Order sheet ({pickedVisible.length || visible.length})
          </button>
          <div className="flex items-stretch border border-border" data-testid="orders-royal-mail">
            <select data-testid="orders-rm-service" value={rmService} onChange={(e) => setRmService(e.target.value)} className="rounded-none border-r border-border bg-background px-2 text-xs">
              {["Tracked 24", "Tracked 48", "Special Delivery Guaranteed by 1pm", "1st Class", "2nd Class"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button data-testid="orders-export-royal-mail" onClick={exportRoyalMail} disabled={rmBusy} className="inline-flex items-center gap-1.5 bg-[#D71920] px-4 py-2 text-xs font-medium text-white disabled:opacity-60">
              {rmBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Royal Mail sheet ({pickedVisible.length || visible.length})
            </button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto border border-border" data-testid="admin-orders">
        <table className="w-full text-sm">
          <thead className="mono bg-muted text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr>
              <th className="p-3"><input type="checkbox" data-testid="orders-select-all" checked={allVisiblePicked} onChange={toggleAll} className="h-4 w-4 accent-primary" title="Select all shown" /></th>
              {["Order", "Placed", "Customer", "Total", "Currency", "Payment", "Fulfillment", "Tracking", ""].map((h, i) => <th key={i} className="p-3 text-left">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visible.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-sm text-muted-foreground" data-testid="orders-empty">No orders match these filters.</td></tr>}
            {visible.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.order_number}`} className={picked[o.id] ? "bg-primary/5" : ""}>
                <td className="p-3"><input type="checkbox" data-testid={`order-select-${o.order_number}`} checked={!!picked[o.id]} onChange={(e) => setPicked({ ...picked, [o.id]: e.target.checked })} className="h-4 w-4 accent-primary" /></td>
                <td className="p-3 mono">{o.order_number}{o.affiliate?.slug && <div className="text-[10px] text-primary" data-testid={`order-via-${o.order_number}`}>via {o.affiliate.brand_name}</div>}</td>
                <td className="p-3 text-xs" data-testid={`order-placed-${o.order_number}`}>
                  {o.created_at ? (<>
                    <div>{new Date(o.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    <div className="mono text-muted-foreground">{new Date(o.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
                  </>) : "—"}
                </td>
                <td className="p-3">{o.customer_name}<div className="text-xs text-muted-foreground">{o.email && !o.email.endsWith("@telegram.dreamlabs.uk") ? o.email : "Telegram order"}</div></td>
                <td className="p-3 mono">{gbp(o.total)}</td>
                <td className="p-3 mono">{o.currency}</td>
                <td className="p-3">
                  <select value={o.payment_status} onChange={(e) => update(o.id, "payment_status", e.target.value)} className="rounded-none border border-border bg-background p-1.5 text-xs" data-testid={`payment-${o.order_number}`}>
                    {["awaiting_payment", "confirming", "paid", "failed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {o.btcpay_invoice_id && (
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground" data-testid={`btcpay-state-${o.order_number}`}>
                      <span className="mono">BTCPay: {o.btcpay_status || "—"}{o.btcpay_additional_status && o.btcpay_additional_status !== "None" ? ` · ${o.btcpay_additional_status}` : ""}{o.paid_late ? " · late payment accepted" : ""}{o.paid_manually_at ? " · marked paid manually" : ""}</span>
                      {o.payment_status !== "paid" && (
                        <button onClick={() => recheck(o)} disabled={!!rechecking[o.id]} data-testid={`recheck-${o.order_number}`}
                          className="inline-flex items-center gap-1 border border-border px-1.5 py-0.5 hover:border-primary">
                          <RefreshCw className={`h-3 w-3 ${rechecking[o.id] ? "animate-spin" : ""}`} /> Re-check
                        </button>
                      )}
                    </div>
                  )}
                  {!o.btcpay_invoice_id && o.payment_error && o.payment_status === "awaiting_payment" && (
                    <div className="mt-1 flex items-center gap-2 text-[10px]" data-testid={`payment-error-${o.order_number}`}>
                      <span className="text-destructive">No invoice</span>
                      <button onClick={() => retryPayment(o)} disabled={!!rechecking[o.id]} data-testid={`retry-payment-${o.order_number}`}
                        className="inline-flex items-center gap-1 border border-destructive/60 px-1.5 py-0.5 text-foreground hover:border-primary">
                        <RefreshCw className={`h-3 w-3 ${rechecking[o.id] ? "animate-spin" : ""}`} /> Retry payment
                      </button>
                    </div>
                  )}
                </td>
                <td className="p-3">
                  <select data-testid={`fulfillment-${o.order_number}`} value={o.fulfillment_status} onChange={(e) => update(o.id, "fulfillment_status", e.target.value)} className="rounded-none border border-border bg-background p-1.5 text-xs">
                    {["pending", "processing", "shipped", "delivered"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <input
                      data-testid={`tracking-input-${o.order_number}`}
                      defaultValue={o.tracking_number || ""}
                      placeholder="tracking #"
                      onChange={(e) => setTracking((t) => ({ ...t, [o.id]: e.target.value }))}
                      className="w-28 rounded-none border border-border bg-background p-1.5 text-xs mono"
                    />
                    <button data-testid={`tracking-save-${o.order_number}`} onClick={() => saveTracking(o.id)} className="border border-border p-1.5 hover:bg-muted"><Save className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
                <td className="p-3">
                  <button data-testid={`order-view-${o.order_number}`} onClick={() => setSelected(o)} className="inline-flex items-center gap-1 border border-border px-2.5 py-1.5 text-xs hover:bg-muted"><Eye className="h-3.5 w-3.5" /> View</button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-muted-foreground">No orders yet</td></tr>}
          </tbody>
        </table>
      </div>
      {selected && <OrderDetail order={selected} onClose={() => setSelected(null)} />}
    </Section>
  );
}

function StrengthRules() {
  const cats = useCategories();
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/pricing-rules").then((r) => setRules(r.data)); }, []);
  if (!rules) return null;
  const setBoost = (code, field, value) => setRules((r) => ({ ...r, boosts: { ...r.boosts, [code]: { ...r.boosts[code], [field]: value } } }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        mix_discount_pct: parseFloat(rules.mix_discount_pct) || 0,
        boosts: Object.fromEntries(Object.entries(rules.boosts).map(([c, b]) => [c, { label: b.label, note: b.note || "", mult: parseFloat(b.mult) || 1, enabled: c === "standard" ? true : b.enabled !== false }])),
        bottle_offer: { ...(rules.bottle_offer || {}), enabled: false },
        guest_bundle_pct: parseFloat(rules.guest_bundle_pct) || 0,
      };
      const { data } = await api.put("/admin/pricing-rules", body);
      setRules(data); toast.success("Pricing rules saved — live on the site now");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const example = 17.5;
  return (
    <div className="border border-border p-4" data-testid="strength-rules">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-heading text-lg font-bold">Strength add-ons, discounts &amp; offers</div>
          <p className="text-xs text-muted-foreground">These labels, descriptions and multipliers drive every price on the site (builders, cart, checkout, emails). Multiplier 1.40 = +40% per bottle.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="strength-rules-save" className="rounded-none">{saving ? "Saving…" : "Save rules"}</Button>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {["x2", "x3"].map((code) => {
          const b = rules.boosts[code];
          const uplift = Math.round(example * ((parseFloat(b.mult) || 1) - 1) * 100) / 100;
          return (
            <div key={code} className={`border border-border p-3 ${b.enabled === false ? "opacity-70" : ""}`} data-testid={`strength-rule-${code}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="mono text-[10px] uppercase tracking-[0.15em] text-primary">{code === "x2" ? "×2" : "×3"} strength</div>
                <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                  <input type="checkbox" data-testid={`strength-enabled-${code}`} checked={b.enabled !== false} onChange={(e) => setBoost(code, "enabled", e.target.checked)} />
                  {b.enabled === false ? "Off everywhere" : "On (site-wide)"}
                </label>
              </div>
              {b.enabled === false && <p className="mono mt-1 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Hidden from every base, Economy and the Telegram bot until switched back on.</p>}
              <div className="mt-2 grid grid-cols-[1fr_1fr_90px] gap-2">
                <div><label className="mono text-[9px] uppercase text-muted-foreground">Label</label><Input data-testid={`strength-label-${code}`} value={b.label} onChange={(e) => setBoost(code, "label", e.target.value)} className="mt-1 rounded-none" /></div>
                <div><label className="mono text-[9px] uppercase text-muted-foreground">Description</label><Input data-testid={`strength-note-${code}`} value={b.note || ""} onChange={(e) => setBoost(code, "note", e.target.value)} placeholder="optional" className="mt-1 rounded-none" /></div>
                <div><label className="mono text-[9px] uppercase text-muted-foreground">Multiplier</label><Input data-testid={`strength-mult-${code}`} type="number" step="0.01" min="1.01" max="5" value={b.mult} onChange={(e) => setBoost(code, "mult", e.target.value)} className="mt-1 rounded-none mono" /></div>
              </div>
              <div className="mono mt-2 text-[10px] text-muted-foreground">Customer sees: <span className="text-foreground">{b.label} · +£{uplift.toFixed(2)} per bottle</span> on a £{example.toFixed(2)} bottle (+{Math.round(((parseFloat(b.mult) || 1) - 1) * 100)}%)</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 border border-border p-3">
        <div><label className="mono text-[9px] uppercase text-muted-foreground">Mixed-size multipack discount %</label><Input data-testid="mix-discount-input" type="number" step="0.5" min="0" max="50" value={rules.mix_discount_pct} onChange={(e) => setRules({ ...rules, mix_discount_pct: e.target.value })} className="mt-1 w-32 rounded-none mono" /></div>
        <p className="text-xs text-muted-foreground">Applied when a multipack or cart mixes 2+ bottle sizes. Shown as “{parseFloat(rules.mix_discount_pct) || 0}% off” on the Multipack cards.</p>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 border border-border p-3" data-testid="trade-volume-rules">
        <div><label className="mono text-[9px] uppercase text-muted-foreground">Trade 10L bundle discount %</label><Input data-testid="trade-10l-input" type="number" step="0.5" min="0" max="50" value={rules.trade_10l_discount_pct ?? 5} onChange={(e) => setRules({ ...rules, trade_10l_discount_pct: e.target.value })} className="mt-1 w-32 rounded-none mono" /></div>
        <div><label className="mono text-[9px] uppercase text-muted-foreground">Trade 20L bundle discount %</label><Input data-testid="trade-20l-input" type="number" step="0.5" min="0" max="50" value={rules.trade_20l_discount_pct ?? 10} onChange={(e) => setRules({ ...rules, trade_20l_discount_pct: e.target.value })} className="mt-1 w-32 rounded-none mono" /></div>
        <p className="max-w-md text-xs text-muted-foreground">Extra saving on the 10L &amp; 20L {cats.deals.title} for logged-in <b>trade accounts only</b>, taken off the prices you set for them. Hidden from regular shoppers.</p>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 border border-border p-3">
        <div><label className="mono text-[9px] uppercase text-muted-foreground">Guest bundle discount %</label><Input data-testid="guest-bundle-input" type="number" step="0.5" min="0" max="50" value={rules.guest_bundle_pct ?? 0} onChange={(e) => setRules({ ...rules, guest_bundle_pct: e.target.value })} className="mt-1 w-32 rounded-none mono" /></div>
        <p className="text-xs text-muted-foreground">Taken off every {cats.guest.title} product when the same order also contains a {cats.build.title}. 0 = off. Applied quietly at checkout — no upsell prompts are shown.</p>
      </div>
    </div>
  );
}

function EconomyConfigAdmin() {
  const cats = useCategories();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/economy-config").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);
  if (!cfg) return null;

  const setPrice = (ml, value) => setCfg((c) => ({
    ...c,
    sizes: c.sizes.map((s) => (s.ml === ml ? { ...s, price: value } : s)),
  }));
  const setEnabled = (ml, enabled) => setCfg((c) => ({
    ...c,
    sizes: c.sizes.map((s) => (s.ml === ml ? { ...s, enabled } : s)),
  }));
  const x2mult = 1 + (parseFloat(cfg.x2_pct) || 0) / 100;
  const x3mult = 1 + (parseFloat(cfg.x3_pct) || 0) / 100;

  const save = async () => {
    setSaving(true);
    try {
      const prices = {};
      cfg.sizes.forEach((s) => { prices[String(s.ml)] = parseFloat(s.price) || 0; });
      const body = {
        prices,
        x2_pct: parseFloat(cfg.x2_pct) || 0,
        x3_pct: parseFloat(cfg.x3_pct) || 0,
        trade_discount_pct: parseFloat(cfg.trade_discount_pct) || 0,
        disabled_ml: cfg.sizes.filter((s) => s.enabled === false).map((s) => s.ml),
        enabled_strengths: cfg.enabled_strengths || ["standard", "x2", "x3"],
      };
      await api.put("/admin/economy-config", body);
      toast.success(`${cats.economy.title} pricing saved — live on the site and bot now`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="border border-border p-4" data-testid="economy-config">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-heading text-lg font-bold">{cats.economy.title} pricing</div>
          <p className="text-xs text-muted-foreground">Flat per-bottle prices for the budget range (Strawberry Vanilla &amp; Unflavoured Clear). Edit any price, switch a size off (it disappears from the Economy page only — Build Your Box is unaffected), change the ×2/×3 uplifts or the trade discount. Applies on the website and the Telegram bot.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="economy-config-save" className="rounded-none">{saving ? "Saving…" : "Save Economy prices"}</Button>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {cfg.sizes.map((s) => (
          <div key={s.ml} className={`border p-3 ${s.enabled === false ? "border-border/50 opacity-60" : "border-border"}`} data-testid={`economy-price-${s.ml}`}>
            <label className="flex cursor-pointer items-center justify-between gap-2 text-sm font-medium">
              <span>{s.label}</span>
              <span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                <input type="checkbox" data-testid={`economy-size-enabled-${s.ml}`} checked={s.enabled !== false} onChange={(e) => setEnabled(s.ml, e.target.checked)} />
                {s.enabled === false ? "Off" : "On"}
              </span>
            </label>
            <label className="mono mt-2 block text-[9px] uppercase text-muted-foreground">Standard £</label>
            <Input data-testid={`economy-price-input-${s.ml}`} type="number" step="0.01" min="0" value={s.price} disabled={s.enabled === false} onChange={(e) => setPrice(s.ml, e.target.value)} className="mt-1 rounded-none mono" />
            <div className="mono mt-1 text-[10px] text-muted-foreground">×2 £{(( parseFloat(s.price) || 0) * x2mult).toFixed(2)} · ×3 £{(((parseFloat(s.price) || 0) * x3mult)).toFixed(2)}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 border border-border p-3">
        <div><label className="mono text-[9px] uppercase text-muted-foreground">×2 strength uplift %</label><Input data-testid="economy-x2-input" type="number" step="1" min="0" max="500" value={cfg.x2_pct} onChange={(e) => setCfg({ ...cfg, x2_pct: e.target.value })} className="mt-1 w-28 rounded-none mono" /></div>
        <div><label className="mono text-[9px] uppercase text-muted-foreground">×3 strength uplift %</label><Input data-testid="economy-x3-input" type="number" step="1" min="0" max="500" value={cfg.x3_pct} onChange={(e) => setCfg({ ...cfg, x3_pct: e.target.value })} className="mt-1 w-28 rounded-none mono" /></div>
        <div><label className="mono text-[9px] uppercase text-muted-foreground">Trade discount %</label><Input data-testid="economy-trade-input" type="number" step="1" min="0" max="95" value={cfg.trade_discount_pct} onChange={(e) => setCfg({ ...cfg, trade_discount_pct: e.target.value })} className="mt-1 w-28 rounded-none mono" /></div>
        <div className="flex flex-col gap-1">
          <span className="mono text-[9px] uppercase text-muted-foreground">Strength boosts offered</span>
          {["x2", "x3"].map((code) => {
            const on = (cfg.enabled_strengths || ["standard", "x2", "x3"]).includes(code);
            return (
              <label key={code} className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid={`economy-strength-${code}`} checked={on}
                  onChange={(e) => setCfg({ ...cfg, enabled_strengths: ["standard", ...["x2", "x3"].filter((c) => (c === code ? e.target.checked : (cfg.enabled_strengths || ["standard", "x2", "x3"]).includes(c)))] })} />
                {code === "x2" ? "×2 Strength" : "×3 Strength"}
              </label>
            );
          })}
        </div>
        <p className="max-w-md text-xs text-muted-foreground">×2 = +{parseFloat(cfg.x2_pct) || 0}% and ×3 = +{parseFloat(cfg.x3_pct) || 0}% of the standard price. Untick a strength to hide it from the Economy builder on the site and bot (the main range's own switches are unaffected; per-flavour switches live on each Economy flavour's edit form under Products). Trade (dynamic-pricing) accounts pay {parseFloat(cfg.trade_discount_pct) || 0}% less across the whole Economy range.</p>
      </div>
    </div>
  );
}

function Pricing() {
  const [sizes, setSizes] = useState([]);
  const quantityOptions = Array.from({ length: 11 }, (_, index) => index + 1);
  const load = () => api.get("/admin/sizes").then((r) => setSizes(r.data));
  useEffect(() => { load(); }, []);

  const setTierPrice = (sid, key, idx, value) => {
    setSizes((prev) => prev.map((s) => {
      if (s.id !== sid) return s;
      const arr = [...(s[key] || [])];
      arr[idx] = { ...arr[idx], price: parseFloat(value) || 0 };
      return { ...s, [key]: arr };
    }));
  };

  const visibleQuantities = (size) =>
    Array.isArray(size.global_visible_quantities)
      ? size.global_visible_quantities.map(Number)
      : quantityOptions;

  const setSizeField = (sid, field, value) => {
    setSizes((prev) =>
      prev.map((size) =>
        size.id === sid ? { ...size, [field]: value } : size
      )
    );
  };

  const toggleQuantity = (sid, quantity, checked) => {
    setSizes((prev) =>
      prev.map((size) => {
        if (size.id !== sid) return size;
        const current = new Set(visibleQuantities(size));
        if (checked) current.add(quantity);
        else current.delete(quantity);
        return {
          ...size,
          global_visible_quantities: [...current].sort((a, b) => a - b),
        };
      })
    );
  };

  const save = async (s) => {
    const body = {
      tiers: s.tiers,
      weight_g: s.weight_g,
      active: s.active !== false,
      hide_when_disabled: !!s.hide_when_disabled,
      global_visible_quantities: visibleQuantities(s),
    };
    if (s.wholesale_tiers) body.wholesale_tiers = s.wholesale_tiers;
    await api.put(`/admin/sizes/${s.id}`, body);
    toast.success(`${s.label} availability and pricing saved`);
    load();
  };

  return (
    <Section title="Tiered Pricing & Weights">
      <div className="space-y-4">
        <DiscountCodesAdmin />
        <StrengthRules />
        <EconomyConfigAdmin />
        {sizes.map((s) => (
          <div key={s.id} className="border border-border p-5" data-testid={`pricing-size-${s.label}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-heading text-lg font-semibold">{s.label} {s.is_wholesale && <span className="mono ml-2 border border-border px-2 py-0.5 text-[10px] uppercase">wholesale</span>}</div>
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={s.active !== false}
                    onChange={(e) => setSizeField(s.id, "active", e.target.checked)}
                  />
                  Size enabled
                </label>
                <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  When disabled:
                  <select data-testid={`size-disabled-mode-${s.label}`} value={s.hide_when_disabled ? "hide" : "grey"} onChange={(e) => setSizeField(s.id, "hide_when_disabled", e.target.value === "hide")}
                    className="rounded-none border border-border bg-background p-1.5 text-xs text-foreground">
                    <option value="grey">Show greyed out — "Sold out · back soon"</option>
                    <option value="hide">Hide it from the shop completely</option>
                  </select>
                </label>
              </div>
              <div className="flex items-center gap-3">
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Weight (g)</label>
                <Input type="number" value={s.weight_g} onChange={(e) => setSizes((p) => p.map((x) => x.id === s.id ? { ...x, weight_g: parseInt(e.target.value) || 0 } : x))} className="w-24 rounded-none" />
                <button data-testid={`pricing-save-${s.label}`} onClick={() => save(s)} className="flex items-center gap-1.5 bg-foreground px-4 py-2 text-sm font-medium text-background"><Save className="h-3.5 w-3.5" /> Save</button>
              </div>
            </div>
            <div className="mt-4 border border-border p-3">
              <div className="mono mb-2 flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <span>Quantities customers can order · click to toggle</span>
                <button type="button" data-testid={`pricing-qty-all-${s.label}`} onClick={() => quantityOptions.forEach((q) => toggleQuantity(s.id, q, visibleQuantities(s).length < quantityOptions.length))} className="border border-border px-2 py-0.5 hover:bg-card">
                  {visibleQuantities(s).length < quantityOptions.length ? "Enable all" : "Disable all"}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {quantityOptions.map((quantity) => {
                  const on = visibleQuantities(s).includes(quantity);
                  return (
                    <button key={quantity} type="button" data-testid={`pricing-qty-${s.label}-${quantity}`} aria-pressed={on} onClick={() => toggleQuantity(s.id, quantity, !on)}
                      className={`mono min-w-[42px] border px-2.5 py-1.5 text-[11px] font-bold transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground line-through hover:border-foreground"}`}>
                      {quantity === 11 ? "11+" : quantity}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 border border-border p-3">
              <div className="mono mb-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Retail price per bottle · by quantity ordered</div>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-7">
                {s.tiers.map((t, i) => (
                  <div key={`retail-${t.min_qty}`}>
                    <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t.min_qty}+ bottles</label>
                    <Input type="number" step="0.01" value={t.price} onChange={(e) => setTierPrice(s.id, "tiers", i, e.target.value)} className="mt-1 rounded-none mono" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Multipacks() {
  const [packs, setPacks] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [form, setForm] = useState({ name: "", size_id: "", bottle_count: 6, fixed_price: 0 });
  const load = useCallback(() => {
    api.get("/multipacks?all=true").then((r) => setPacks(r.data));
    api.get("/sizes").then((r) => {
      setSizes(r.data);
      setForm((f) => (f.size_id ? f : { ...f, size_id: r.data[0]?.id || "" }));
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.size_id) return toast.error("Name & size required");
    try { await api.post("/admin/multipacks", { ...form, bottle_count: parseInt(form.bottle_count), fixed_price: parseFloat(form.fixed_price), active: true }); load(); toast.success("Multipack created"); setForm({ ...form, name: "", fixed_price: 0 }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { await api.delete(`/admin/multipacks/${id}`); load(); toast.success("Deleted"); };
  const sizeLabel = (id) => sizes.find((s) => s.id === id)?.label || "—";

  return (
    <Section title="Multipack Offers">
      <div className="flex flex-wrap items-end gap-3 border border-border p-4" data-testid="multipack-form">
        <div className="flex-1 min-w-[160px]"><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Name</label><Input data-testid="mp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-none" /></div>
        <div><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Size</label><select data-testid="mp-size" value={form.size_id} onChange={(e) => setForm({ ...form, size_id: e.target.value })} className="mt-1 block rounded-none border border-border bg-background p-2.5 text-sm">{sizes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
        <div><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Bottles</label><Input data-testid="mp-count" type="number" value={form.bottle_count} onChange={(e) => setForm({ ...form, bottle_count: e.target.value })} className="mt-1 w-24 rounded-none" /></div>
        <div><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Fixed £</label><Input data-testid="mp-price" type="number" step="0.01" value={form.fixed_price} onChange={(e) => setForm({ ...form, fixed_price: e.target.value })} className="mt-1 w-28 rounded-none" /></div>
        <button data-testid="mp-add-btn" onClick={create} className="flex items-center gap-1.5 bg-foreground px-5 py-2.5 text-sm font-medium text-background"><Plus className="h-4 w-4" /> Create</button>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
        {packs.map((p) => (
          <div key={p.id} className="bg-card p-5" data-testid={`admin-mp-${p.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-heading text-lg font-semibold">{p.name}</div><div className="mono text-xs text-muted-foreground">{p.bottle_count} × {sizeLabel(p.size_id)}</div></div>
              <button data-testid={`mp-delete-${p.id}`} onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="mono mt-3 text-2xl font-black">{gbp(p.fixed_price)}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Account() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Section title="Account · Change Password">
      <div className="max-w-md border border-border p-6" data-testid="change-password-form">
        <p className="mb-4 text-sm text-muted-foreground">Set a strong admin password. This saves to the database and persists across redeploys.</p>
        <div className="space-y-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Current password</label>
            <Input data-testid="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 rounded-none" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">New password</label>
            <Input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 rounded-none" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Confirm new password</label>
            <Input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-none" />
          </div>
          <button data-testid="cp-submit" disabled={saving} onClick={submit} className="flex items-center gap-1.5 bg-foreground px-5 py-2.5 text-sm font-medium text-background disabled:opacity-40">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Update Password"}
          </button>
        </div>
      </div>
    </Section>
  );
}

function Shipping() {
  const [zones, setZones] = useState([]);
  const load = () => api.get("/admin/shipping-zones").then((r) => setZones(r.data));
  useEffect(() => { load(); }, []);

  const setBracketPrice = (zid, idx, value) => {
    setZones((prev) => prev.map((z) => {
      if (z.id !== zid) return z;
      const brackets = [...z.brackets];
      brackets[idx] = { ...brackets[idx], price: parseFloat(value) || 0 };
      return { ...z, brackets };
    }));
  };
  const setZoneField = (zid, field, value) => {
    setZones((prev) => prev.map((z) => z.id === zid ? { ...z, [field]: value } : z));
  };

  const zoneCountries = (zone) =>
    typeof zone.countries === "string"
      ? zone.countries.split(",").map((country) => country.trim()).filter(Boolean)
      : zone.countries || [];

  const setCountryEnabled = (zid, country, enabled) => {
    setZones((prev) =>
      prev.map((zone) => {
        if (zone.id !== zid) return zone;
        const disabled = new Set(zone.disabled_countries || []);
        if (enabled) disabled.delete(country);
        else disabled.add(country);
        return { ...zone, disabled_countries: [...disabled] };
      })
    );
  };

  const save = async (z) => {
    const countries = zoneCountries(z);
    const body = {
      name: z.name, tier: z.tier, sort: z.sort,
      countries,
      disabled_countries: (z.disabled_countries || []).filter((country) =>
        countries.includes(country)
      ),
      brackets: z.brackets.map((b) => ({ min_weight: parseInt(b.min_weight), max_weight: parseInt(b.max_weight), price: parseFloat(b.price), label: b.label || "" })),
      free_shipping_threshold: z.free_shipping_threshold === "" || z.free_shipping_threshold == null ? null : parseFloat(z.free_shipping_threshold),
    };
    await api.put(`/admin/shipping-zones/${z.id}`, body);
    load(); toast.success(`${z.name} saved`);
  };

  return (
    <Section title="Shipping Zones · Country + Weight Pricing">
      <div className="space-y-4" data-testid="admin-shipping-zones">
        {zones.map((z) => (
          <div key={z.id} className="border border-border p-5" data-testid={`zone-${z.tier}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="font-heading text-lg font-semibold">Tier {z.tier} · {z.name}</div>
              <div className="flex items-center gap-3">
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Free ship over £</label>
                <Input data-testid={`zone-free-${z.tier}`} type="number" step="0.01" value={z.free_shipping_threshold ?? ""} placeholder="none" onChange={(e) => setZoneField(z.id, "free_shipping_threshold", e.target.value)} className="w-28 rounded-none" />
                <button data-testid={`zone-save-${z.tier}`} onClick={() => save(z)} className="flex items-center gap-1.5 bg-foreground px-4 py-2 text-sm font-medium text-background"><Save className="h-3.5 w-3.5" /> Save</button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {z.brackets.map((b, i) => (
                <div key={`${b.min_weight}-${i}`}>
                  <label className="mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">{b.label || `${b.min_weight}-${b.max_weight}g`} (£)</label>
                  <Input data-testid={`zone-${z.tier}-bracket-${i}`} type="number" step="0.01" value={b.price} onChange={(e) => setBracketPrice(z.id, i, e.target.value)} className="mt-1 rounded-none" />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Countries (comma-separated)</label>
              <textarea
                data-testid={`zone-countries-${z.tier}`}
                defaultValue={zoneCountries(z).join(", ")}
                onChange={(e) => setZoneField(z.id, "countries", e.target.value)}
                className="mt-1 w-full rounded-none border border-border bg-background p-2 text-xs mono"
                rows={2}
              />
              <div className="mono mb-2 mt-4 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                Countries enabled for ordering
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                {zoneCountries(z).map((country) => (
                  <label key={country} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!(z.disabled_countries || []).includes(country)}
                      onChange={(e) =>
                        setCountryEnabled(z.id, country, e.target.checked)
                      }
                    />
                    {country}
                  </label>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

const parseTelegramIds = (raw) => String(raw ?? "").split(/[\s,;]+/).map((v) => parseInt(v, 10)).filter((n) => Number.isFinite(n) && n > 0);

const CATEGORY_META = [
  ["build", "Build Your Box (single-size boxes)"],
  ["deals", "Multipack Deals (mixed-size presets · retail only)"],
  ["guest", "Premium Brands (guest products)"],
  ["economy", "Economy Flavours (budget range)"],
];

function Content() {
  const { refresh: refreshSiteContent } = useSiteContent();
  const [c, setC] = useState(null);
  const [bases, setBases] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/content").then((r) => setC(r.data || {}));
  const loadBases = () => api.get("/admin/bases").then((r) => setBases(r.data));
  useEffect(() => { load(); loadBases(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setFeature = (i, k, v) => setC((p) => {
    const features = [...(p.features || [])];
    features[i] = { ...features[i], [k]: v };
    return { ...p, features };
  });
  const setBaseField = (i, k, v) => setBases((prev) => prev.map((b, idx) => idx === i ? { ...b, [k]: v } : b));

  const toggleBaseStrength = (index, strength, enabled) => {
    setBases((prev) =>
      prev.map((base, currentIndex) => {
        if (currentIndex !== index) return base;
        const strengths = new Set(
          Array.isArray(base.enabled_strengths)
            ? base.enabled_strengths
            : ["standard", "x2", "x3"]
        );
        if (enabled) strengths.add(strength);
        else strengths.delete(strength);
        return { ...base, enabled_strengths: [...strengths] };
      })
    );
  };

  const saveBases = async () => {
    try {
      await Promise.all(bases.map((b) => api.put(`/admin/bases/${b.code}`, {
        name: b.name,
        description: b.description || "",
        active: b.active !== false,
        enabled_strengths: Array.isArray(b.enabled_strengths)
          ? b.enabled_strengths
          : ["standard", "x2", "x3"],
      })));
      toast.success("Base and strength availability saved");
      loadBases();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadBanner = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload?kind=banner", fd);
      set("hero_image_url", data.image_url);
      toast.success("Banner uploaded — hit Save All to publish it");
    } catch (e) { toast.error(apiError(e)); }
  };

  const setCategory = (key, field, v) => setC((p) => ({ ...p, categories: { ...(p.categories || {}), [key]: { ...((p.categories || {})[key] || {}), [field]: v } } }));
  const resetCategory = (key) => setC((p) => ({ ...p, categories: { ...(p.categories || {}), [key]: { ...DEFAULT_CATEGORIES[key] } } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content", c); toast.success("Site content saved — live across the site"); load(); refreshSiteContent(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!c) return null;
  const field = (k, label, testid, textarea) => (
    <div>
      <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</label>
      {textarea
        ? <textarea data-testid={testid} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} rows={3} className="mt-1 w-full rounded-none border border-border bg-background p-2 text-sm" />
        : <Input data-testid={testid} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-none" />}
    </div>
  );

  return (
    <Section title="Site Content" action={<button data-testid="content-save" onClick={save} disabled={saving} className="flex items-center gap-1.5 bg-foreground px-5 py-2.5 text-sm font-medium text-background disabled:opacity-40"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save All"}</button>}>
      <div className="space-y-6">
        <div className="border border-border p-5">
          <div className="mono mb-4 text-[11px] uppercase tracking-[0.2em]">Announcement Bar</div>
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="ann-enabled" type="checkbox" checked={!!c.announcement_enabled} onChange={(e) => set("announcement_enabled", e.target.checked)} /> Enabled
            </label>
            <div><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Background</label><input type="color" value={c.announcement_bg || "#0A0A0A"} onChange={(e) => set("announcement_bg", e.target.value)} className="mt-1 block h-10 w-16 border border-border bg-transparent" /></div>
            <div><label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Text color</label><input type="color" value={c.announcement_fg || "#FAFAFA"} onChange={(e) => set("announcement_fg", e.target.value)} className="mt-1 block h-10 w-16 border border-border bg-transparent" /></div>
          </div>
          <div className="mt-4">{field("announcement_text", "Announcement text (rolls across the top)", "ann-text", true)}</div>
        </div>

        <SiteCopyEditor c={c} set={set} />

        <BotWordingEditor c={c} set={set} />

        <div className="border border-border p-5" data-testid="content-delivery-options-section">
          <div className="mono mb-1 text-[11px] uppercase tracking-[0.2em]">Delivery-details options</div>
          <p className="mb-3 text-xs text-muted-foreground">Checkout always offers "Type it here — encrypted in your browser" (PGP done automatically on the customer's device) and "paste your own PGP block". The temp.pm one-time-link option can be paused here when temp.pm is down — it disappears from the website checkout and the Telegram bot within a minute.</p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="content-temp-pm-enabled" checked={c.temp_pm_enabled !== false} onChange={(e) => set("temp_pm_enabled", e.target.checked)} className="h-4 w-4 accent-primary" />
            Offer temp.pm one-time links
          </label>
        </div>

        <div className="border border-border p-5" data-testid="content-support-section">
          <div className="mono mb-1 text-[11px] uppercase tracking-[0.2em]">Support contact</div>
          <p className="mb-4 text-xs text-muted-foreground">Shown in the footer, on order status / tracking pages and in order emails for the main shop. Partner shops show their own contact instead (set per partner under Dropshippers). Leave all blank to hide.</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {field("support_telegram", "Telegram username", "support-telegram")}
            {field("support_email", "Support email", "support-email")}
            {field("support_url", "Contact link (https://…)", "support-url")}
          </div>
        </div>

        <div className="border border-border p-5" data-testid="home-banner-section">
          <div className="mono mb-1 text-[11px] uppercase tracking-[0.2em]">Homepage Banner</div>
          <p className="mb-4 text-xs text-muted-foreground">Shown full-width at the top of the homepage, above "How would you like to order?". Upload a wide image (around 2000 × 730px works best) — it is kept at its own shape, never cropped.</p>
          {c.hero_image_url ? (
            <div className="overflow-hidden border border-border bg-black" data-testid="home-banner-preview">
              <img src={c.hero_image_url} alt="Homepage banner" className="block h-auto w-full" />
            </div>
          ) : (
            <div className="border border-dashed border-border p-6 text-center text-xs text-muted-foreground" data-testid="home-banner-empty">No banner set — the homepage starts straight at the ordering options.</div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="mono cursor-pointer border border-border px-3 py-2 text-[10px] uppercase tracking-[0.15em] hover:border-foreground">
              <input type="file" accept="image/*" className="hidden" data-testid="home-banner-upload" onChange={(e) => uploadBanner(e.target.files)} /> {c.hero_image_url ? "Replace banner" : "Upload banner"}
            </label>
            {c.hero_image_url && (
              <button type="button" data-testid="home-banner-remove" onClick={() => set("hero_image_url", "")} className="mono border border-border px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:border-red-500 hover:text-red-600">Remove banner</button>
            )}
          </div>
        </div>

        <div className="border border-border p-5" data-testid="category-names-section">
          <div className="mono mb-1 text-[11px] uppercase tracking-[0.2em]">Order Categories</div>
          <p className="mb-4 text-xs text-muted-foreground">Rename the four ordering options. The name, tagline and description are used everywhere on the site — homepage chooser, trade dashboard, page headings, cart nudges, footer, How to Order and the trade price sheet / welcome PDF.</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {CATEGORY_META.map(([key, hint]) => {
              const cat = (c.categories || {})[key] || {};
              const d = DEFAULT_CATEGORIES[key];
              return (
                <div key={key} className="border border-border p-4" data-testid={`category-editor-${key}`}>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{hint}</div>
                    <button type="button" data-testid={`category-reset-${key}`} onClick={() => resetCategory(key)} className="mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground">Reset</button>
                  </div>
                  <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Name</label>
                  <Input data-testid={`category-title-${key}`} value={cat.title ?? d.title} onChange={(e) => setCategory(key, "title", e.target.value)} className="mt-1 rounded-none" />
                  <label className="mono mt-3 block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Tagline (small text above the name)</label>
                  <Input data-testid={`category-tagline-${key}`} value={cat.tagline ?? d.tagline} onChange={(e) => setCategory(key, "tagline", e.target.value)} className="mt-1 rounded-none" />
                  <label className="mono mt-3 block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Description</label>
                  <textarea data-testid={`category-desc-${key}`} value={cat.desc ?? d.desc} onChange={(e) => setCategory(key, "desc", e.target.value)} rows={3} className="mt-1 w-full rounded-none border border-border bg-background p-2 text-sm" />
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-border p-5">
          <div className="mono mb-4 flex items-center justify-between text-[11px] uppercase tracking-[0.2em]">
            <span>Bases / Formulas</span>
            <button data-testid="bases-save" onClick={saveBases} className="flex items-center gap-1.5 border border-border px-3 py-1.5 text-[11px] font-medium normal-case hover:bg-card"><Save className="h-3.5 w-3.5" /> Save bases</button>
          </div>
          <div className="mb-4 max-w-xs">
            {field("base_label", 'Label for "Base" (e.g. Base, Formula, Type)', "base-label-input")}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {bases.map((b, i) => (
              <div key={b.code} className="border border-border p-3" data-testid={`base-editor-${b.code}`}>
                <div className="mono mb-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Code: {b.code}</div>
                <label className="mb-3 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={b.active !== false}
                    onChange={(e) => setBaseField(i, "active", e.target.checked)}
                  />
                  Base enabled
                </label>
                <div className="mb-3 flex flex-wrap gap-3">
                  {[
                    ["standard", "1×"],
                    ["x2", "2×"],
                    ["x3", "3×"],
                  ].map(([strength, label]) => (
                    <label key={strength} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={
                          !Array.isArray(b.enabled_strengths)
                          || b.enabled_strengths.includes(strength)
                        }
                        onChange={(e) =>
                          toggleBaseStrength(i, strength, e.target.checked)
                        }
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <Input data-testid={`base-name-${b.code}`} value={b.name || ""} onChange={(e) => setBaseField(i, "name", e.target.value)} className="rounded-none" placeholder="Display name" />
                <textarea data-testid={`base-desc-${b.code}`} value={b.description || ""} onChange={(e) => setBaseField(i, "description", e.target.value)} rows={2} className="mt-2 w-full rounded-none border border-border bg-background p-2 text-sm" placeholder="Note / description shown to customers" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.email} is now ${role}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Section title="Customers & Wholesale Access">
      <p className="mb-4 text-sm text-muted-foreground">Tag customers as <strong>Wholesale</strong> to unlock trade pricing across the entire site for their account. Wholesale users see reduced prices on every size when logged in.</p>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="mono border-b border-border bg-muted text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-testid="users-table">
            {users.filter((u) => u.role !== "dropship").map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.email}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.name}</td>
                <td className="px-4 py-3">
                  <span className={`mono border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${u.role === "wholesale" ? "border-green-600 text-green-600" : u.role === "admin" ? "border-foreground" : "border-border text-muted-foreground"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  {u.role === "admin" ? (
                    <span className="mono text-[10px] uppercase text-muted-foreground">—</span>
                  ) : u.role === "wholesale" ? (
                    <button data-testid={`revoke-wholesale-${u.id}`} onClick={() => setRole(u, "customer")} className="border border-border px-3 py-1.5 text-[11px] font-medium hover:bg-card">Revoke wholesale</button>
                  ) : (
                    <button data-testid={`make-wholesale-${u.id}`} onClick={() => setRole(u, "wholesale")} className="bg-foreground px-3 py-1.5 text-[11px] font-medium text-background hover:-translate-y-0.5 transition-transform">Make wholesale</button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


const dropshipperContactEmail = (email) =>
  email && !email.endsWith("@dropship.local") ? email : "";

function Dropshippers() {
  const cats = useCategories();
  const [list, setList] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [selectedDropshipperId, setSelectedDropshipperId] = useState(null);
  const [screen, setScreen] = useState("list");
  const [form, setForm] = useState({ username: "", name: "", email: "", password: "", telegram_user_id: "", telegram_user_ids: "", telegram_username: "", discount_pct: 0, overrides: {} });
  const [edits, setEdits] = useState({});
  const [pwEdit, setPwEdit] = useState({});
  const [guestFlavors, setGuestFlavors] = useState([]);
  const [presets, setPresets] = useState([]);
  const [tradeRates, setTradeRates] = useState({ ten: 5, twenty: 10 });

  const load = useCallback(() => {
    api.get("/admin/dropshippers").then((r) => {
      const dropshippers = r.data || [];
      setList(dropshippers);
      setSelectedDropshipperId((current) =>
        dropshippers.some((dropshipper) => dropshipper.id === current)
          ? current
          : null
      );
    });
    api.get("/sizes").then((r) => setSizes((r.data || []).filter((s) => (s.ml || 0) < 10000)));
    api.get("/flavors").then((r) => setGuestFlavors((r.data || []).filter((f) => f.range_type === "guest" && (f.guest_price || 0) > 0))).catch(() => {});
    api.get("/admin/pricing-model-presets").then((r) => setPresets(r.data || [])).catch(() => {});
    api.get("/pricing-rules").then((r) => setTradeRates({
      ten: Number(r.data?.trade_10l_discount_pct) || 0,
      twenty: Number(r.data?.trade_20l_discount_pct) || 0,
    })).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const overrideQtys = Array.from({ length: 11 }, (_, i) => i + 1);

  const setCreateVisibility = (sizeId, quantity, checked) => {
    setForm((current) => {
      const overrides = { ...current.overrides };
      const existing = overrides[sizeId];
      const ladder =
        typeof existing === "object" && existing !== null
          ? { ...existing }
          : existing !== undefined && existing !== null && existing !== ""
            ? { all: existing }
            : {};
      const currentVisible = Array.isArray(ladder.visible)
        ? ladder.visible.map(Number)
        : [...overrideQtys];
      const visible = new Set(currentVisible);

      if (checked) visible.add(quantity);
      else visible.delete(quantity);

      ladder.visible = [...visible].sort((a, b) => a - b);
      overrides[sizeId] = ladder;
      return { ...current, overrides };
    });
  };

  const retailPrice = (size, quantity) => {
    const tiers = [...(size.tiers || [])].sort((a, b) => a.min_qty - b.min_qty);
    let price = tiers.length ? tiers[0].price : 0;
    tiers.forEach((tier) => {
      if (quantity >= tier.min_qty) price = tier.price;
    });
    return price;
  };

  const setCreateOverride = (sizeId, quantity, value) => {
    setForm((current) => {
      const overrides = { ...current.overrides };
      const existing = overrides[sizeId];
      const ladder = typeof existing === "object" && existing !== null
        ? { ...existing }
        : {};

      if (value === "") delete ladder[String(quantity)];
      else ladder[String(quantity)] = value;

      if (Object.keys(ladder).length) overrides[sizeId] = ladder;
      else delete overrides[sizeId];

      return { ...current, overrides };
    });
  };

  const create = async () => {
    try {
      const { data } = await api.post("/admin/dropshippers", {
        username: form.username.trim(), name: form.name.trim(), password: form.password,
        email: form.email.trim() || null,
        telegram_user_id: form.telegram_user_id === "" ? null : parseInt(form.telegram_user_id, 10),
        telegram_user_ids: parseTelegramIds(form.telegram_user_ids),
        telegram_username: form.telegram_username.trim() || null,
        discount_pct: parseFloat(form.discount_pct) || 0,
        overrides: form.overrides,
      });
      toast.success(`Dynamic pricing customer ${form.username} created`);
      if (form.email.trim()) {
        if (data?.onboarding_email_sent) toast.success("Onboarding email sent (welcome + price sheet PDF)");
        else toast.message("Account created — onboarding email not sent. Check the address/verified domain, or use ‘Email onboarding’.");
      }
      setForm({ username: "", name: "", email: "", password: "", telegram_user_id: "", telegram_user_ids: "", telegram_username: "", discount_pct: 0, overrides: {} });
      load();
      setScreen("list");
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendOnboarding = async (d) => {
    try { const { data } = await api.post(`/admin/dropshippers/${d.id}/onboarding-email`); toast.success(`Onboarding email sent to ${data.email}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async (d) => {
    const e = edits[d.id] || {};
    try {
      await api.put(`/admin/dropshippers/${d.id}`, {
        name: e.name ?? d.name,
        email: (e.email ?? dropshipperContactEmail(d.email)).trim() || null,
        telegram_user_id: (e.telegram_user_id ?? d.telegram_user_id ?? "") === ""
          ? null
          : parseInt(e.telegram_user_id ?? d.telegram_user_id, 10),
        telegram_user_ids: parseTelegramIds(e.telegram_user_ids ?? (d.telegram_user_ids || []).join(", ")),
        telegram_username: (e.telegram_username ?? d.telegram_username ?? "").trim() || null,
        discount_pct: parseFloat(e.discount_pct ?? d.discount_pct) || 0,
        overrides: e.overrides ?? d.overrides ?? {},
        pricing_model_enabled: (e.pricing_model_enabled ?? d.pricing_model) ? true : false,
        pricing_model_per_litre: parseFloat(e.pricing_model_per_litre ?? d.pricing_model_config?.per_litre) || 0,
        pricing_model_bottle_fee: parseFloat(e.pricing_model_bottle_fee ?? d.pricing_model_config?.bottle_fee) || 0,
        pricing_model_escalator_pct_step: parseFloat(e.pricing_model_escalator_pct_step ?? d.pricing_model_config?.escalator_pct_step) || 0,
        pricing_model_escalator_l_step: parseFloat(e.pricing_model_escalator_l_step ?? d.pricing_model_config?.escalator_l_step) || 5,
        premium_overrides: e.premium_overrides ?? d.premium_overrides ?? {},
        economy_discount_pct: (e.economy_discount_pct ?? d.economy_discount_pct ?? "") === ""
          ? null
          : parseFloat(e.economy_discount_pct ?? d.economy_discount_pct),
        economy_overrides: e.economy_overrides ?? d.economy_overrides ?? {},
        ...(e.affiliate ? { affiliate: {
          ...e.affiliate,
          commission_pct: parseFloat(e.affiliate.commission_pct) || 0,
          slug: (e.affiliate.slug || "").trim() || null,
          payout_fee_gbp: e.affiliate.payout_fee_gbp === "" || e.affiliate.payout_fee_gbp == null ? null : parseFloat(e.affiliate.payout_fee_gbp) || 0,
        } } : {}),
      });
      toast.success(`${d.username} account saved`);
      setEdits((p) => ({ ...p, [d.id]: undefined }));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const resetPw = async (d) => {
    const pw = pwEdit[d.id];
    if (!pw || pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/admin/dropshippers/${d.id}/password`, { password: pw });
      toast.success(`${d.username} password reset`);
      setPwEdit((p) => ({ ...p, [d.id]: "" }));
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete dynamic pricing customer "${d.username}"?`)) return;
    try {
      await api.delete(`/admin/dropshippers/${d.id}`);
      if (selectedDropshipperId === d.id) setSelectedDropshipperId(null);
      setScreen("list");
      toast.success("Deleted");
      load();
    }
    catch (err) { toast.error(apiError(err)); }
  };

  const overridesSummary = (overrides) => {
    const lines = [];
    for (const s of sizes) {
      const ov = overrides?.[s.id];
      if (ov == null || ov === "") continue;
      if (typeof ov === "object") {
        if (ov.all != null && ov.all !== "") {
          lines.push(`${s.label} — £${Number(ov.all).toFixed(2)} per bottle (any quantity)`);
        } else {
          Object.keys(ov)
            .filter((k) => k !== "visible" && k !== "all" && ov[k] !== "" && ov[k] != null)
            .sort((a, b) => Number(a) - Number(b))
            .forEach((q) => lines.push(`${s.label} ×${q === "11" ? "11+" : q} — £${Number(ov[q]).toFixed(2)} per bottle`));
        }
      } else {
        lines.push(`${s.label} — £${Number(ov).toFixed(2)} per bottle`);
      }
    }
    return lines;
  };

  const printWelcomeCard = (d, password) => {
    const pct = parseFloat(d.discount_pct) || 0;
    const overrideLines = overridesSummary(d.overrides || {});
    const pctLine = d.pricing_model
      ? `<li>Your account has its own <b>custom trade pricing</b> — see your personal price sheet for exact per-bottle prices.</li>`
      : pct > 0
      ? `<li><b>${pct}% off</b> every standard price across the whole website — applied automatically once you log in.</li>`
      : "";
    const overrideBlock = (!d.pricing_model && overrideLines.length)
      ? `<div class="box"><div class="lbl">Your special per-size prices</div><ul>${overrideLines.map((l) => `<li>${l}</li>`).join("")}</ul></div>`
      : "";
    const pwLine = password && password.trim()
      ? `<span class="mono">${password.trim()}</span>`
      : `<span class="mono" style="letter-spacing:2px">____________</span> <span class="muted">(the password you were given)</span>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Trade Welcome — ${d.name || d.username}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0a0a0a;margin:0;padding:32px;background:#fff}
  .card{max-width:640px;margin:0 auto;border:2px solid #0a0a0a;padding:36px 40px}
  .eyebrow{font-family:monospace;font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#666}
  h1{font-size:30px;font-weight:800;letter-spacing:-0.5px;margin:6px 0 2px}
  .sub{font-size:14px;color:#444;margin:0 0 24px}
  .box{border:1px solid #ddd;padding:16px 18px;margin:14px 0}
  .lbl{font-family:monospace;font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#888;margin-bottom:6px}
  .big{font-size:20px;font-weight:800}
  .mono{font-family:monospace;font-weight:700}
  ul{margin:8px 0 0;padding-left:18px;font-size:14px;line-height:1.7}
  ol{font-size:14px;line-height:1.8;padding-left:18px}
  .muted{color:#888;font-weight:400}
  .foot{margin-top:22px;font-family:monospace;font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#aaa;border-top:1px solid #eee;padding-top:14px}
  .no-print{position:fixed;top:12px;right:12px;z-index:99}
  .no-print button{font-family:inherit;font-size:13px;font-weight:700;padding:9px 16px;border:2px solid #0a0a0a;background:#0a0a0a;color:#fff;cursor:pointer;border-radius:6px}
  @media print{body{padding:0}.card{border:2px solid #0a0a0a}.no-print{display:none}}
</style></head><body>
<div class="no-print"><button onclick="window.print()">⬇ Download / Print PDF</button></div>
<div class="card">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0a0a0a;padding-bottom:14px;margin-bottom:18px">
    <div style="font-weight:900;font-size:22px;letter-spacing:-0.5px;line-height:1">DREAMLABS<span style="color:#7c3aed">.UK</span><div style="font-family:monospace;font-size:8px;letter-spacing:2px;color:#888;font-weight:400;margin-top:4px">LAB-CRAFTED · CRYPTO ONLY</div></div>
    <div style="text-align:center"><img src="https://api.qrserver.com/v1/create-qr-code/?size=90x90&margin=0&data=https%3A%2F%2Fdreamlabs.uk%2FTradeAccount" width="72" height="72" alt="QR to login" style="display:block"/><div style="font-family:monospace;font-size:8px;letter-spacing:1px;color:#888;margin-top:3px">SCAN TO LOG IN</div></div>
  </div>
  <div class="eyebrow">DreamLabs UK · Trade Account</div>
  <h1>Welcome, ${d.name || d.username}</h1>
  <p class="sub">Your private trade account is ready. Keep this card safe — it has everything you need to log in and see your special pricing.</p>

  <div class="box">
    <div class="lbl">Where to log in</div>
    <div class="big mono">dreamlabs.uk/TradeAccount</div>
  </div>
  <div class="box">
    <div class="lbl">Your username</div>
    <div class="big mono">${d.username}</div>
    <div class="lbl" style="margin-top:14px">Your password</div>
    <div class="big">${pwLine}</div>
  </div>

  <div class="box">
    <div class="lbl">How your pricing works</div>
    <ul>
      ${pctLine}
      <li>Your prices show <b>automatically</b> on every product and at checkout while you're logged in — no codes to enter.</li>
      <li>Payment is <b>Bitcoin or Litecoin only</b>.</li>
      <li>Delivery details are sent <b>encrypted</b> at checkout for your privacy.</li>
    </ul>
  </div>
  ${overrideBlock}

  <div class="box">
    <div class="lbl">Getting started</div>
    <ol>
      <li>Go to <b>dreamlabs.uk/TradeAccount</b>.</li>
      <li>Enter your username and password above.</li>
      <li>Shop as normal — your trade prices apply everywhere.</li>
    </ol>
  </div>

  <div class="foot">DreamLabs UK · Lab-crafted · Crypto only · Keep this card private</div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
</body></html>`;
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) { toast.error("Please allow pop-ups to print the welcome card"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const printPriceSheet = (d) => {
    const dsUnit = (size, qty) => {
      const ov = (d.overrides || {})[size.id];
      const pct = parseFloat(d.discount_pct) || 0;
      if (ov != null && typeof ov !== "object" && ov !== "") return Number(ov);
      if (ov && typeof ov === "object") {
        if (ov.all != null && ov.all !== "") return Number(ov.all);
        const key = qty <= 10 ? String(qty) : "11";
        if (ov[key] != null && ov[key] !== "") return Number(ov[key]);
      }
      const retail = tierUnitPrice(size.tiers || [], qty);
      return Math.round(retail * (1 - pct / 100) * 100) / 100;
    };
    // Prefer the authoritative, model-aware prices the backend computed for this account.
    const strengthCols = (d.strength_columns && d.strength_columns.length) ? d.strength_columns : [{ code: "standard", label: "Standard", mult: 1 }];
    const strengthHead = strengthCols.map((c) => `<th class="r">${c.label}</th>`).join("");
    const rows = (d.price_rows && d.price_rows.length)
      ? d.price_rows.map((r) => `<tr><td>${r.label}</td>${strengthCols.map((c) => `<td class="r mono">${gbp((r.by_strength || {})[c.code] ?? r.price * c.mult)}</td>`).join("")}</tr>`).join("")
      : [...sizes].sort((a, b) => (a.ml || 0) - (b.ml || 0)).map((s) => `<tr><td>${s.label}</td>${strengthCols.map((c) => `<td class="r mono">${gbp(dsUnit(s, 11) * c.mult)}</td>`).join("")}</tr>`).join("");
    const premiumHtml = (d.premium_rows && d.premium_rows.length)
      ? `<div class="lbl" style="margin-top:20px;font-weight:900;font-size:13px;color:#0a0a0a">${cats.guest.title} — price per bottle by quantity</div>
         ${d.premium_rows.map((r) => `<div style="margin:8px 0"><div style="font-weight:700">${r.brand ? r.brand + " · " : ""}${r.name}</div><div class="mono" style="font-size:13px;color:#333;margin-top:2px">${(r.tiers || []).map((t) => `${t.range}: ${gbp(t.price)}`).join(" &nbsp; ")}</div>${(r.options || []).length ? `<div class="sub" style="margin-top:3px">Optional add-ons (per bottle): ${r.options.map((o) => `${o.label} +${gbp(o.price)}`).join(", ")}</div>` : ""}</div>`).join("")}`
      : "";
    const economyHtml = (d.economy_rows && d.economy_rows.length)
      ? `<div class="lbl" style="margin-top:20px;font-weight:900;font-size:13px;color:#0a0a0a">${cats.economy.title} — price per bottle</div>
         <table><thead><tr><th>Size</th>${((d.economy_strength_columns && d.economy_strength_columns.length) ? d.economy_strength_columns : [{ code: "standard", label: "Standard", mult: 1 }]).map((c) => `<th class="r">${c.label}</th>`).join("")}</tr></thead><tbody>${d.economy_rows.map((r) => `<tr><td>${r.label}</td>${((d.economy_strength_columns && d.economy_strength_columns.length) ? d.economy_strength_columns : [{ code: "standard", mult: 1 }]).map((c) => `<td class="r mono">${gbp((r.by_strength || {})[c.code] ?? r.price * c.mult)}</td>`).join("")}</tr>`).join("")}</tbody></table>`
      : "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Trade Prices — ${d.name || d.username}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0a0a0a;margin:0;padding:32px;background:#fff}
  .card{max-width:640px;margin:0 auto;border:2px solid #0a0a0a;padding:36px 40px}
  .eyebrow{font-family:monospace;font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#666}
  h1{font-size:28px;font-weight:800;letter-spacing:-0.5px;margin:6px 0 2px}
  .sub{font-size:13px;color:#444;margin:0 0 22px}
  table{width:100%;border-collapse:collapse;margin:6px 0 4px}
  th,td{padding:8px 10px;border-bottom:1px solid #eee;font-size:14px;text-align:left}
  th{font-family:monospace;font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#888}
  .r{text-align:right}
  .box{border:1px solid #ddd;padding:14px 16px;margin:12px 0}
  .lbl{font-family:monospace;font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#888}
  .big{font-size:22px;font-weight:800}
  .muted{color:#999}
  .save{font-family:monospace;font-size:11px;font-weight:700;color:#166534;background:#dcfce7;padding:2px 8px;border-radius:999px;margin-left:8px}
  .mono{font-family:monospace}
  .foot{margin-top:22px;font-family:monospace;font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#aaa;border-top:1px solid #eee;padding-top:14px}
  .no-print{position:fixed;top:12px;right:12px;z-index:99}
  .no-print button{font-family:inherit;font-size:13px;font-weight:700;padding:9px 16px;border:2px solid #0a0a0a;background:#0a0a0a;color:#fff;cursor:pointer;border-radius:6px}
  @media print{body{padding:0}.card{border:2px solid #0a0a0a}.no-print{display:none}}
</style></head><body>
<div class="no-print"><button onclick="window.print()">⬇ Download / Print PDF</button></div>
<div class="card">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0a0a0a;padding-bottom:14px;margin-bottom:18px">
    <div style="font-weight:900;font-size:22px;letter-spacing:-0.5px;line-height:1">DREAMLABS<span style="color:#7c3aed">.UK</span><div style="font-family:monospace;font-size:8px;letter-spacing:2px;color:#888;font-weight:400;margin-top:4px">LAB-CRAFTED · CRYPTO ONLY</div></div>
    <div style="text-align:center"><img src="https://api.qrserver.com/v1/create-qr-code/?size=90x90&margin=0&data=https%3A%2F%2Fdreamlabs.uk%2FTradeAccount" width="72" height="72" alt="QR to login" style="display:block"/><div style="font-family:monospace;font-size:8px;letter-spacing:1px;color:#888;margin-top:3px">SCAN TO LOG IN</div></div>
  </div>
  <div class="eyebrow">DreamLabs UK · Trade Price Sheet</div>
  <p class="sub">Your account prices, effective while logged in at dreamlabs.uk/TradeAccount.</p>
  <div class="lbl" style="font-weight:900;font-size:13px;color:#0a0a0a">DreamLabs Flavours — price per bottle</div>
  <table><thead><tr><th>Size</th>${strengthHead}</tr></thead><tbody>${rows}</tbody></table>
  ${premiumHtml}
  ${economyHtml}
  <div class="foot">DreamLabs UK · Crypto only · Please keep private</div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
</body></html>`;
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) { toast.error("Please allow pop-ups to print the price sheet"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const setEditField = (id, key, value) => setEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), [key]: value } }));

  const setPremiumOverride = (id, flavorId, value, base) => setEdits((p) => {
    const cur = p[id]?.premium_overrides ?? base ?? {};
    return { ...p, [id]: { ...(p[id] || {}), premium_overrides: { ...cur, [flavorId]: value } } };
  });
  const setEconomyOverride = (id, ml, value, base) => setEdits((p) => {
    const cur = p[id]?.economy_overrides ?? base ?? {};
    return { ...p, [id]: { ...(p[id] || {}), economy_overrides: { ...cur, [String(ml)]: value } } };
  });
  const clearEconomyOverrides = (id) => setEditField(id, "economy_overrides", {});
  const applyPreset = (id, preset) => setEdits((p) => ({ ...p, [id]: { ...(p[id] || {}),
    pricing_model_enabled: true,
    pricing_model_per_litre: preset.per_litre,
    pricing_model_bottle_fee: preset.bottle_fee,
    pricing_model_escalator_pct_step: preset.escalator_pct_step,
    pricing_model_escalator_l_step: preset.escalator_l_step } }));
  const resetToStandardModel = (id) => setEdits((p) => ({ ...p, [id]: { ...(p[id] || {}),
    pricing_model_enabled: true, pricing_model_per_litre: 100, pricing_model_bottle_fee: 2.5,
    pricing_model_escalator_pct_step: 5, pricing_model_escalator_l_step: 5 } }));
  const clearAllPrices = (id) => setEditField(id, "overrides", {});
  const savePreset = async (d) => {
    const e = edits[d.id] || {}; const cfg = d.pricing_model_config || {};
    const name = window.prompt("Save this pricing model as a preset. Name?");
    if (!name) return;
    try {
      const { data } = await api.post("/admin/pricing-model-presets", {
        name,
        per_litre: parseFloat(e.pricing_model_per_litre ?? cfg.per_litre) || 0,
        bottle_fee: parseFloat(e.pricing_model_bottle_fee ?? cfg.bottle_fee) || 0,
        escalator_pct_step: parseFloat(e.pricing_model_escalator_pct_step ?? cfg.escalator_pct_step) || 0,
        escalator_l_step: parseFloat(e.pricing_model_escalator_l_step ?? cfg.escalator_l_step) || 5,
      });
      setPresets(data || []);
      toast.success(`Preset "${name}" saved`);
    } catch (err) { toast.error(apiError(err)); }
  };
  const setEditOverride = (id, sizeId, quantity, value, current) => setEdits((previous) => {
    const base = previous[id]?.overrides ?? current ?? {};
    const overrides = { ...base };
    const existing = overrides[sizeId];
    const ladder = typeof existing === "object" && existing !== null
      ? { ...existing }
      : {};

    if (value === "" || value == null) delete ladder[String(quantity)];
    else ladder[String(quantity)] = value;

    if (Object.keys(ladder).length) overrides[sizeId] = ladder;
    else delete overrides[sizeId];

    return {
      ...previous,
      [id]: { ...(previous[id] || {}), overrides },
    };
  });

  const setEditVisibility = (
    id,
    sizeId,
    quantity,
    checked,
    current
  ) => setEdits((previous) => {
    const base = previous[id]?.overrides ?? current ?? {};
    const overrides = { ...base };
    const existing = overrides[sizeId];
    const ladder =
      typeof existing === "object" && existing !== null
        ? { ...existing }
        : existing !== undefined && existing !== null && existing !== ""
          ? { all: existing }
          : {};
    const currentVisible = Array.isArray(ladder.visible)
      ? ladder.visible.map(Number)
      : [...overrideQtys];
    const visible = new Set(currentVisible);

    if (checked) visible.add(quantity);
    else visible.delete(quantity);

    ladder.visible = [...visible].sort((a, b) => a - b);
    overrides[sizeId] = ladder;

    return {
      ...previous,
      [id]: { ...(previous[id] || {}), overrides },
    };
  });

  const renderOverrideGrid = (values, onPriceChange, onVisibilityChange, modelOn = false) => (
    <div className="space-y-3">
      {sizes.map((size) => {
        const existing = values?.[size.id];
        const ladder = typeof existing === "object" && existing !== null
          ? existing
          : existing !== undefined && existing !== null && existing !== ""
            ? { all: existing }
            : {};
        const visible = Array.isArray(ladder.visible) ? ladder.visible.map(Number) : [...overrideQtys];
        const allPrice = ladder.all ?? "";
        const hasAllPrice = allPrice !== "";
        const anyShown = visible.length > 0;
        const showPrices = !modelOn && !hasAllPrice;

        return (
          <div key={size.id} className={`border p-4 ${anyShown ? "border-border" : "border-border/50 opacity-60"}`} data-testid={`ds-size-${size.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <div className="font-heading text-base font-bold">{size.label}</div>
                <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                  <input type="checkbox" data-testid={`ds-toggle-all-${size.id}`} checked={anyShown}
                    onChange={(e) => overrideQtys.forEach((q) => onVisibilityChange(size.id, q, e.target.checked))} />
                  {anyShown ? "Available to this account" : "Hidden from this account"}
                </label>
              </div>
              {!modelOn && (
                <div className="flex items-center gap-2">
                  <label className="mono text-[10px] uppercase text-muted-foreground">One price, any qty</label>
                  <span className="mono text-xs text-muted-foreground">£</span>
                  <Input type="number" step="0.01" value={allPrice} placeholder="optional" data-testid={`ds-all-price-${size.id}`}
                    onChange={(event) => onPriceChange(size.id, "all", event.target.value)} className="w-28 rounded-none mono" />
                </div>
              )}
            </div>

            {anyShown && (
              <div className="mt-3 grid grid-cols-6 gap-1.5 sm:grid-cols-11" data-testid={`ds-qty-grid-${size.id}`}>
                {overrideQtys.map((quantity) => {
                  const isShown = visible.includes(quantity);
                  return (
                    <div key={quantity} className="min-w-0">
                      <button type="button" aria-pressed={isShown} data-testid={`ds-qty-${size.id}-${quantity}`}
                        onClick={() => onVisibilityChange(size.id, quantity, !isShown)}
                        className={`mono w-full border px-1 py-1.5 text-[11px] font-bold transition-colors ${isShown ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground line-through hover:border-foreground"}`}>
                        {quantity === 11 ? "11+" : quantity}
                      </button>
                      {showPrices && (
                        <Input type="number" step="0.01" disabled={!isShown} data-testid={`ds-price-${size.id}-${quantity}`}
                          value={ladder[String(quantity)] ?? ""} placeholder={`${retailPrice(size, quantity)}`}
                          onChange={(event) => onPriceChange(size.id, quantity, event.target.value)}
                          className="mt-1 h-8 w-full rounded-none px-1 text-center text-xs mono" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {anyShown && showPrices && <p className="mono mt-1.5 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">£ per bottle at each quantity · blank = retail minus the account discount % (greyed number is retail)</p>}
            {anyShown && hasAllPrice && !modelOn && <p className="mono mt-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Every enabled quantity is charged £{Number(allPrice).toFixed(2)} per bottle</p>}
            {anyShown && modelOn && <p className="mono mt-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Prices come from the per-litre model above · only quantity availability applies here</p>}
          </div>
        );
      })}
    </div>
  );

  const sortedDropshippers = [...list].sort((left, right) => {
    const leftName = left.name || left.username || "";
    const rightName = right.name || right.username || "";
    return leftName.localeCompare(rightName, undefined, { sensitivity: "base" });
  });
  const selectedDropshipper = list.find(
    (dropshipper) => dropshipper.id === selectedDropshipperId
  );

  const returnToList = () => {
    setSelectedDropshipperId(null);
    setScreen("list");
  };

  if (screen === "create") {
    return (
      <div data-testid="dynamic-pricing-create-screen">
        <button
          type="button"
          onClick={returnToList}
          className="mt-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dynamic Pricing
        </button>
        <Section title="Add Dynamic Pricing Customer">
        <p className="mb-4 text-sm text-muted-foreground">
          Website access uses a <strong>username &amp; password</strong>. Telegram bot access is approved using the customer's
          <strong> numeric Telegram user ID</strong>; the email and @username are shown as contact details. Set an account
          <strong> discount %</strong> off retail and optionally override prices for quantities 1–10 and 11+.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Username</label>
            <Input data-testid="ds-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 rounded-none" placeholder="no spaces" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Display name</label>
            <Input data-testid="ds-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-none" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Email</label>
            <Input data-testid="ds-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-none" placeholder="name@example.com" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Telegram @username</label>
            <Input data-testid="ds-telegram-username" value={form.telegram_username} onChange={(e) => setForm({ ...form, telegram_username: e.target.value })} className="mt-1 rounded-none" placeholder="@username" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Telegram user ID</label>
            <Input data-testid="ds-telegram-id" type="number" step="1" value={form.telegram_user_id}
              onChange={(e) => setForm({ ...form, telegram_user_id: e.target.value })}
              className="mt-1 rounded-none" placeholder="numeric ID" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">More Telegram user IDs</label>
            <Input data-testid="ds-telegram-ids" value={form.telegram_user_ids}
              onChange={(e) => setForm({ ...form, telegram_user_ids: e.target.value })}
              className="mt-1 rounded-none mono" placeholder="e.g. 123456789, 987654321" />
            <div className="mt-1 text-xs text-muted-foreground">Optional — for customers who use more than one Telegram account. All of them get this account's prices.</div>
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Password</label>
            <Input data-testid="ds-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-none" placeholder="min 6 chars" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Discount % off retail</label>
            <Input data-testid="ds-discount" type="number" step="0.5" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className="mt-1 rounded-none" />
          </div>
        </div>
        <div className="mt-5">
          <div className="mono mb-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            Sizes, quantities &amp; per-bottle prices for this account · blank price = retail minus the discount %
          </div>
          {renderOverrideGrid(
            form.overrides,
            (sizeId, quantity, value) =>
              setCreateOverride(sizeId, quantity, value),
            (sizeId, quantity, checked) =>
              setCreateVisibility(sizeId, quantity, checked)
          )}
        </div>
        <button data-testid="ds-create-btn" onClick={create} className="mt-5 flex items-center gap-1.5 bg-foreground px-4 py-2.5 text-sm font-medium text-background transition-transform hover:-translate-y-0.5">
          <Plus className="h-4 w-4" /> Create customer
        </button>
      </Section>
      </div>
    );
  }

  if (screen === "details" && selectedDropshipper) {
    const d = selectedDropshipper;
    const e = edits[d.id] || {};
    const curOverrides = e.overrides ?? d.overrides ?? {};

    return (
      <div data-testid="dynamic-pricing-detail-screen">
        <button
          type="button"
          data-testid="dynamic-pricing-back"
          onClick={returnToList}
          className="mt-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dynamic Pricing
        </button>

        <Section
          title={`Dynamic Pricing — ${d.name || d.username}`}
          action={(
            <div className="flex items-center gap-2">
              <button data-testid={`ds-welcome-${d.id}`} onClick={() => printWelcomeCard(d, pwEdit[d.id])} className="flex items-center gap-1.5 border border-border px-4 py-2 text-sm font-medium hover:bg-card">
                <Printer className="h-3.5 w-3.5" /> Welcome card
              </button>
              <button data-testid={`ds-pricesheet-${d.id}`} onClick={() => printPriceSheet(d)} className="flex items-center gap-1.5 border border-border px-4 py-2 text-sm font-medium hover:bg-card">
                <Printer className="h-3.5 w-3.5" /> Price sheet
              </button>
              {d.email && !String(d.email).endsWith("@dropship.local") && (
                <button data-testid={`ds-onboarding-${d.id}`} onClick={() => sendOnboarding(d)} title={`Email welcome + price sheet PDF to ${d.email}`} className="flex items-center gap-1.5 border border-border px-4 py-2 text-sm font-medium hover:bg-card">
                  <Printer className="h-3.5 w-3.5" /> Email onboarding
                </button>
              )}
              <button data-testid={`ds-save-${d.id}`} onClick={() => saveEdit(d)} className="flex items-center gap-1.5 bg-foreground px-4 py-2 text-sm font-medium text-background">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
              <button data-testid={`ds-delete-${d.id}`} onClick={() => remove(d)} className="border border-border p-2 text-muted-foreground hover:text-red-600" aria-label={`Delete ${d.name || d.username}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}
        >
          <div className="border border-border p-5" data-testid={`ds-row-${d.id}`}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Website username</div>
                <div className="mt-1 font-medium">{d.username}</div>
              </div>
              <div className="w-full sm:w-40">
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Discount %</label>
                <Input data-testid={`ds-edit-discount-${d.id}`} type="number" step="0.5" value={e.discount_pct ?? d.discount_pct}
                  onChange={(ev) => setEditField(d.id, "discount_pct", ev.target.value)} className="mt-1 rounded-none" />
              </div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Display name</label>
                <Input data-testid={`ds-edit-name-${d.id}`} value={e.name ?? d.name ?? ""}
                  onChange={(ev) => setEditField(d.id, "name", ev.target.value)} className="mt-1 rounded-none" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Email</label>
                <Input data-testid={`ds-edit-email-${d.id}`} type="email" value={e.email ?? dropshipperContactEmail(d.email)}
                  onChange={(ev) => setEditField(d.id, "email", ev.target.value)} className="mt-1 rounded-none" placeholder="name@example.com" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Telegram @username</label>
                <Input data-testid={`ds-edit-telegram-username-${d.id}`} value={e.telegram_username ?? d.telegram_username ?? ""}
                  onChange={(ev) => setEditField(d.id, "telegram_username", ev.target.value)} className="mt-1 rounded-none" placeholder="@username" />
                <div className="mt-1 text-xs text-muted-foreground">Display only; the numeric ID authorizes bot access.</div>
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Telegram user ID</label>
                <Input data-testid={`ds-edit-telegram-${d.id}`} type="number" step="1" value={e.telegram_user_id ?? d.telegram_user_id ?? ""}
                  onChange={(ev) => setEditField(d.id, "telegram_user_id", ev.target.value)} className="mt-1 rounded-none" placeholder="Not linked" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">More Telegram user IDs</label>
                <Input data-testid={`ds-edit-telegram-ids-${d.id}`} value={e.telegram_user_ids ?? (d.telegram_user_ids || []).join(", ")}
                  onChange={(ev) => setEditField(d.id, "telegram_user_ids", ev.target.value)} className="mt-1 rounded-none mono" placeholder="e.g. 123456789, 987654321" />
                <div className="mt-1 text-xs text-muted-foreground">Comma-separated. Every ID here logs into the bot as this account.</div>
              </div>
            </div>

            <div className="mt-6 border-t border-border pt-5">
              <div className="font-heading text-lg font-semibold">Prices for {d.name || d.username}</div>

              {(() => {
                const ed = edits[d.id] || {};
                const modelOn = (ed.pricing_model_enabled ?? d.pricing_model) ? true : false;
                const cfg = d.pricing_model_config || {};
                const val = (k, dflt) => ed[k] ?? cfg[k] ?? dflt;
                return (
                  <div className="mb-4 mt-3 border border-border p-4" data-testid={`ds-model-${d.id}`}>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input type="checkbox" data-testid={`ds-model-toggle-${d.id}`} checked={modelOn}
                        onChange={(ev) => setEditField(d.id, "pricing_model_enabled", ev.target.checked)} />
                      <span className="font-medium">Use per-litre pricing model (Build Your Box)</span>
                    </label>
                    <p className="mt-1 text-xs text-muted-foreground">When on, bottle prices = £/litre × size + a per-bottle fee, plus a rolling basket volume discount. This overrides the % &amp; per-size grid below. 2.5L/5L bottles keep their built-in discount.</p>
                    {modelOn && (
                      <>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                          <div>
                            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Price per litre (£)</label>
                            <Input data-testid={`ds-model-perlitre-${d.id}`} type="number" step="0.5" min="0" value={val("pricing_model_per_litre", 100)}
                              onChange={(ev) => setEditField(d.id, "pricing_model_per_litre", ev.target.value)} className="mt-1 rounded-none mono" />
                          </div>
                          <div>
                            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Per-bottle fee (£)</label>
                            <Input data-testid={`ds-model-fee-${d.id}`} type="number" step="0.25" min="0" value={val("pricing_model_bottle_fee", 2.5)}
                              onChange={(ev) => setEditField(d.id, "pricing_model_bottle_fee", ev.target.value)} className="mt-1 rounded-none mono" />
                          </div>
                          <div>
                            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Volume discount +%</label>
                            <Input data-testid={`ds-model-pct-${d.id}`} type="number" step="1" min="0" value={val("pricing_model_escalator_pct_step", 5)}
                              onChange={(ev) => setEditField(d.id, "pricing_model_escalator_pct_step", ev.target.value)} className="mt-1 rounded-none mono" />
                          </div>
                          <div>
                            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">…for every (L)</label>
                            <Input data-testid={`ds-model-step-${d.id}`} type="number" step="0.5" min="0.5" value={val("pricing_model_escalator_l_step", 5)}
                              onChange={(ev) => setEditField(d.id, "pricing_model_escalator_l_step", ev.target.value)} className="mt-1 rounded-none mono" />
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <select data-testid={`ds-preset-select-${d.id}`} defaultValue="" className="rounded-none border border-border bg-background px-2 py-1.5 text-sm"
                            onChange={(ev) => { const pr = presets.find((x) => x.name === ev.target.value); if (pr) applyPreset(d.id, pr); ev.target.value = ""; }}>
                            <option value="">Apply a preset…</option>
                            {presets.map((pr) => <option key={pr.name} value={pr.name}>{pr.name} (£{pr.per_litre}/L +£{pr.bottle_fee})</option>)}
                          </select>
                          <button type="button" data-testid={`ds-preset-save-${d.id}`} onClick={() => savePreset(d)} className="mono border border-border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-primary hover:text-primary">Save as preset</button>
                          <button type="button" data-testid={`ds-model-reset-${d.id}`} onClick={() => resetToStandardModel(d.id)} className="mono border border-border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-primary hover:text-primary">Reset to standard (£100/L · £2.50 · +5%/5L)</button>
                        </div>
                        <div className="mt-3">
                          <div className="mono text-[10px] uppercase tracking-[0.15em] text-primary">Current prices per bottle (saved) · by strength</div>
                          <table className="mt-1 w-full text-left" data-testid={`ds-model-price-table-${d.id}`}>
                            <thead>
                              <tr className="mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
                                <th className="py-1 font-normal">Size</th>
                                {(d.strength_columns || []).map((c) => <th key={c.code} className="py-1 text-right font-normal">{c.code === "standard" ? "Std" : c.label.split(" ")[0]}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {(d.price_rows || []).map((r) => (
                                <tr key={r.ml} className="border-b border-border/50">
                                  <td className="mono py-1 text-xs text-muted-foreground">{r.label}</td>
                                  {(d.strength_columns || []).map((c) => (
                                    <td key={c.code} className={`mono py-1 text-right text-sm ${c.code === "standard" ? "font-bold text-primary" : "text-foreground"}`} data-testid={c.code === "standard" ? `ds-model-price-${d.id}-${r.ml}` : `ds-model-price-${d.id}-${r.ml}-${c.code}`}>
                                      {gbp((r.by_strength || {})[c.code] ?? r.price * c.mult)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="mt-1 text-[11px] text-muted-foreground">Change the fields above and hit Save to update these.</p>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {(() => {
                const modelOn = ((edits[d.id] || {}).pricing_model_enabled ?? d.pricing_model) ? true : false;
                return (
                  <>
                    <div className="mono mb-3 mt-1 flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      <span>{modelOn ? "Sizes & quantities this account can order" : "Sizes, quantities & per-bottle prices for this account"}</span>
                      {!modelOn && <button type="button" data-testid={`ds-clear-prices-${d.id}`} onClick={() => clearAllPrices(d.id)} className="border border-border px-2.5 py-1 font-bold tracking-[0.12em] text-muted-foreground hover:border-red-500 hover:text-red-600">Clear all prices</button>}
                    </div>
                    {renderOverrideGrid(
                      curOverrides,
                      (sizeId, quantity, value) => setEditOverride(d.id, sizeId, quantity, value, d.overrides),
                      (sizeId, quantity, checked) => setEditVisibility(d.id, sizeId, quantity, checked, d.overrides),
                      modelOn
                    )}
                  </>
                );
              })()}
            </div>

            <div className="mt-6 border-t border-border pt-5" data-testid={`ds-premium-${d.id}`}>
              <div className="font-heading text-lg font-semibold">{cats.guest.title} prices for {d.name || d.username}</div>
              <p className="mono mb-3 mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                Set an exact price per Premium product · blank = they pay the standard price
              </p>
              {guestFlavors.length === 0 ? (
                <p className="text-xs text-muted-foreground">No Premium products yet.</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {guestFlavors.map((f) => {
                    const pov = (edits[d.id]?.premium_overrides ?? d.premium_overrides ?? {});
                    return (
                      <div key={f.id} className="flex items-center gap-2 border border-border p-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{f.name}</div>
                          <div className="mono text-[10px] text-muted-foreground">standard {gbp(f.guest_price || 0)}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="mono text-xs text-muted-foreground">£</span>
                          <Input data-testid={`ds-premium-price-${d.id}-${f.id}`} type="number" step="0.01" min="0"
                            value={pov[f.id] ?? ""} placeholder={(f.guest_price || 0).toFixed(2)}
                            onChange={(ev) => setPremiumOverride(d.id, f.id, ev.target.value, d.premium_overrides)}
                            className="w-24 rounded-none mono" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-6 border-t border-border pt-5" data-testid={`ds-economy-${d.id}`}>
              <div className="font-heading text-lg font-semibold">{cats.economy.title} prices for {d.name || d.username}</div>
              <p className="mono mb-3 mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                A % discount off Economy for everything, or an exact price per size (blank size = uses the % / global)
              </p>
              <div className="mb-4 flex flex-wrap items-end gap-2">
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Economy discount %</label>
                  <Input data-testid={`ds-economy-discount-${d.id}`} type="number" step="1" min="0" max="95"
                    value={edits[d.id]?.economy_discount_pct ?? (d.economy_discount_pct ?? "")}
                    placeholder={`global ${d.economy_global_discount_pct ?? 0}%`}
                    onChange={(ev) => setEditField(d.id, "economy_discount_pct", ev.target.value)}
                    className="mt-1 w-32 rounded-none mono" />
                </div>
                <span className="mono pb-2 text-[10px] text-muted-foreground">blank = global {d.economy_global_discount_pct ?? 0}% off</span>
              </div>
              {(d.economy_sizes || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No Economy sizes configured yet.</p>
              ) : (
                <>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {(d.economy_sizes || []).map((s) => {
                      const eov = (edits[d.id]?.economy_overrides ?? d.economy_overrides ?? {});
                      return (
                        <div key={s.ml} className="flex items-center gap-2 border border-border p-2">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{s.label}</div>
                            <div className="mono text-[10px] text-muted-foreground">retail {gbp(s.retail || 0)}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="mono text-xs text-muted-foreground">£</span>
                            <Input data-testid={`ds-economy-price-${d.id}-${s.ml}`} type="number" step="0.01" min="0"
                              value={eov[String(s.ml)] ?? ""} placeholder="std"
                              onChange={(ev) => setEconomyOverride(d.id, s.ml, ev.target.value, d.economy_overrides)}
                              className="w-24 rounded-none mono" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button data-testid={`ds-economy-clear-${d.id}`} onClick={() => clearEconomyOverrides(d.id)}
                    className="mono mt-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive">
                    Clear all Economy price overrides
                  </button>
                  <p className="mt-1 text-[10px] text-muted-foreground">Exact prices are the Standard-strength price; ×2 / ×3 uplifts are added on top automatically.</p>
                </>
              )}
            </div>

            <AffiliateAdmin d={d} value={edits[d.id]?.affiliate ?? d.affiliate}
              onChange={(patch) => setEditField(d.id, "affiliate", { ...(edits[d.id]?.affiliate ?? d.affiliate ?? {}), ...patch })} />

            <div className="mt-5 flex items-end gap-3 border-t border-border pt-5">
              <div className="max-w-xs flex-1">
                <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Reset password</label>
                <Input data-testid={`ds-newpw-${d.id}`} type="text" value={pwEdit[d.id] || ""} placeholder="new password"
                  onChange={(ev) => setPwEdit((p) => ({ ...p, [d.id]: ev.target.value }))} className="mt-1 rounded-none" />
              </div>
              <button data-testid={`ds-resetpw-${d.id}`} onClick={() => resetPw(d)} className="flex items-center gap-1.5 border border-border px-4 py-2 text-sm font-medium hover:bg-card">
                <KeyRound className="h-3.5 w-3.5" /> Reset
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Tip: type the password here, hit Reset, then click <b>Welcome card</b> above to print a handout with the login URL, username and this password.</p>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div data-testid="dynamic-pricing-list-screen">
      <Section
        title="Dynamic Pricing"
        action={(
          <button
            type="button"
            data-testid="dynamic-pricing-add-customer"
            onClick={() => {
              setSelectedDropshipperId(null);
              setScreen("create");
            }}
            className="flex items-center gap-1.5 bg-foreground px-4 py-2 text-sm font-medium text-background transition-transform hover:-translate-y-0.5"
          >
            <Plus className="h-4 w-4" /> Add customer
          </button>
        )}
      >
        <p className="mb-4 text-sm text-muted-foreground">
          Select a customer to open their own pricing structure.
        </p>

        {list.length === 0 && <p className="border border-dashed border-border p-5 text-sm text-muted-foreground">No dynamic pricing customers yet.</p>}
        {list.length > 0 && (
          <div className="border border-border" data-testid="dropshippers-list" role="list">
              {sortedDropshippers.map((dropshipper) => {
                const contact = [
                  dropshipperContactEmail(dropshipper.email),
                  dropshipper.telegram_username
                    ? `@${dropshipper.telegram_username}`
                    : null,
                ].filter(Boolean).join(" · ");

                return (
                  <div key={dropshipper.id} role="listitem" className="border-b border-border last:border-b-0">
                    <button
                      type="button"
                      data-testid={`ds-select-${dropshipper.id}`}
                      onClick={() => {
                        setSelectedDropshipperId(dropshipper.id);
                        setScreen("details");
                      }}
                      className="flex w-full items-center justify-between gap-5 p-4 text-left transition-colors hover:bg-card"
                    >
                      <span className="min-w-0">
                        <span className="block font-heading text-base font-semibold">{dropshipper.name || dropshipper.username}</span>
                        <span className="mono mt-1 block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{dropshipper.username}</span>
                        {contact && (
                          <span className="mt-2 block break-all text-xs text-muted-foreground">
                            {contact}
                          </span>
                        )}
                      </span>
                      <span className="flex shrink-0 items-center gap-2 text-sm font-medium">
                        Open pricing <ChevronRight className="h-4 w-4" />
                      </span>
                    </button>
                  </div>
                );
              })}
          </div>
        )}
      </Section>
    </div>
  );
}
